import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Newspaper, LayoutDashboard, Settings, Package, Image, Calendar,
  LogOut, Eye, EyeOff, TrendingUp, Users, DollarSign, BarChart3,
  Instagram, Globe, ChevronRight, Bell, Menu, X, Send, Loader2,
  MapPin, Clock, Building2, MessageSquare, Check, Sparkles, FileText,
  CheckCircle, Play, CreditCard, Plus, Edit2, Trash2, Save, Upload,
  Lock, Link as LinkIcon, Facebook, Twitter, Youtube, Music, Camera,
  AlertCircle, ExternalLink, Heart, Share2, MessageCircle, Disc3, 
  Briefcase, Search, Star, Filter, Phone, Mail, Award, Headphones, ArrowLeft, Info
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import ContractChat from '../../components/ContractChat';
import NotificationBell from '../../components/NotificationBell';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_rebill-payment-fix/artifacts/vodl6hhv_Blanco-Icono.png";

// Image with fallback component
const ImageWithFallback = ({ src, alt, className, fallbackIcon: FallbackIcon = Image }) => {
  const [error, setError] = useState(false);
  
  if (error || !src) {
    return (
      <div className={`flex flex-col items-center justify-center bg-zinc-800 text-zinc-500 ${className}`}>
        <FallbackIcon size={24} className="mb-1 opacity-50" />
        <span className="text-xs">Imagen no disponible</span>
      </div>
    );
  }
  
  return (
    <img 
      src={src} 
      alt={alt} 
      className={className}
      onError={() => setError(true)}
    />
  );
};

// ============ Press Events Tab Component ============
const PressEventsTab = ({ press }) => {
  const [searchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [offers, setOffers] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [selectedContractForChat, setSelectedContractForChat] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState('events');
  const [sendingOffer, setSendingOffer] = useState(false);
  const [offerForm, setOfferForm] = useState({
    package_ids: [],
    message: '',
    proposed_price: ''
  });

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'contratos') {
      setActiveSubTab('contracts');
    }
  }, [searchParams]);

  // Listen for custom event from dashboard shortcut
  useEffect(() => {
    const handleSwitchToContracts = () => {
      setActiveSubTab('contracts');
    };
    window.addEventListener('switchToContractsSubTab', handleSwitchToContracts);
    return () => {
      window.removeEventListener('switchToContractsSubTab', handleSwitchToContracts);
    };
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [eventsRes, quotesRes, offersRes, contractsRes] = await Promise.all([
        api.get('/press/events/available'),
        api.get('/press/quotes/press'),
        api.get('/press/offers/press'),
        api.get('/press/contracts/press')
      ]);
      setEvents(eventsRes.data || []);
      setQuotes(quotesRes.data || []);
      setOffers(offersRes.data || []);
      setContracts(contractsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openOfferModal = (event) => {
    setSelectedEvent(event);
    setOfferForm({
      package_ids: [],
      message: '',
      proposed_price: ''
    });
    setShowOfferModal(true);
  };

  const sendOffer = async () => {
    // Message is optional if packages are selected
    if (offerForm.package_ids.length === 0 && !offerForm.message.trim()) {
      toast.error('Seleccioná al menos un paquete o escribí un mensaje');
      return;
    }

    // Validate minimum price of $1,000
    const price = parseFloat(offerForm.proposed_price);
    if (offerForm.proposed_price && (isNaN(price) || price < 1000)) {
      toast.error('El precio mínimo de oferta es $1,000 ARS');
      return;
    }

    setSendingOffer(true);
    try {
      // Don't auto-generate message for package-only offers
      const messageToSend = offerForm.message.trim() || '';
      
      await api.post('/press/offers/create', {
        producer_id: selectedEvent.producer_id,
        event_id: selectedEvent.id,
        package_ids: offerForm.package_ids,
        message: messageToSend,
        proposed_price: offerForm.proposed_price ? parseFloat(offerForm.proposed_price) : null
      });
      
      toast.success('Oferta enviada correctamente');
      setShowOfferModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar oferta');
    } finally {
      setSendingOffer(false);
    }
  };

  const respondToQuote = async (quoteId, response) => {
    try {
      await api.post(`/press/quotes/${quoteId}/respond`, response);
      toast.success('Cotización enviada');
      fetchData();
    } catch (error) {
      toast.error('Error al responder');
    }
  };

  // Simplified flow for package-only quotes
  const acceptPackageQuote = async (quoteId) => {
    try {
      const response = await api.post(`/press/quotes/${quoteId}/accept-package`);
      toast.success(`Cotización aceptada por $${response.data.quote_amount?.toLocaleString()}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aceptar');
    }
  };

  const rejectPackageQuote = async (quoteId) => {
    try {
      await api.post(`/press/quotes/${quoteId}/reject-package`);
      toast.success('Cotización rechazada');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al rechazar');
    }
  };

  // Check if quote is package-only (simplified flow)
  const isPackageOnlyQuote = (quote) => {
    const hasPackages = quote.package_ids && quote.package_ids.length > 0;
    const hasNoBudget = !quote.budget_range;
    const hasNoMessage = !quote.message || quote.message.trim() === '';
    return hasPackages && hasNoBudget && hasNoMessage;
  };

  // Calculate package total for display
  const calculatePackageTotal = (packageIds) => {
    if (!packageIds || !press?.service_packages) return 0;
    let total = 0;
    for (const pkgId of packageIds) {
      const pkg = press.service_packages.find(p => p.id === pkgId);
      if (pkg) total += pkg.price || 0;
    }
    return total;
  };

  // Get package names from IDs
  const getPackageNames = (packageIds) => {
    if (!packageIds || !press?.service_packages) return [];
    return packageIds.map(pkgId => {
      const pkg = press.service_packages.find(p => p.id === pkgId);
      return pkg?.name || '';
    }).filter(Boolean);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: 'Pendiente', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
      quoted: { label: 'Cotizado', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
      accepted: { label: 'Aceptado', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
      rejected: { label: 'Rechazado', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
      paid: { label: 'Pagado', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
    };
    const c = config[status] || config.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs border ${c.color}`}>
        {c.label}
      </span>
    );
  };

  const getContractStatusBadge = (status) => {
    const config = {
      pending_payment: { label: 'Pendiente de Pago', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: CreditCard },
      active: { label: 'Activo', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
      in_progress: { label: 'En Progreso', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Play },
      completed: { label: 'Completado', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: Check },
      cancelled: { label: 'Cancelado', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: X },
    };
    const c = config[status] || config.pending_payment;
    const Icon = c.icon;
    return (
      <span className={`px-2 py-1 rounded-full text-xs border ${c.color} flex items-center gap-1`}>
        <Icon size={12} />
        {c.label}
      </span>
    );
  };

  const updateContractStatus = async (contractId, status) => {
    try {
      await api.post(`/press/contracts/${contractId}/update-status`, { status });
      toast.success(`Contrato ${status === 'completed' ? 'completado' : status === 'in_progress' ? 'en progreso' : 'actualizado'}`);
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar contrato');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div>
        <h1 className="text-2xl font-black text-white mb-2">Eventos Disponibles</h1>
        <p className="text-zinc-400">Explorá eventos donde podés ofrecer tus servicios</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-green-400" size={32} />
        </div>
      ) : (
        <>
          {/* Events List */}
          <div className="space-y-4">
            {events.length === 0 ? (
              <div className="text-center py-16 text-zinc-500">
                <Calendar className="mx-auto mb-4 text-zinc-600" size={48} />
                <p>No hay eventos disponibles</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {events.map((event) => (
                  <div 
                    key={event.id} 
                    className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden hover:border-green-500/30 transition-all"
                  >
                      <div className="h-32 bg-gradient-to-br from-green-500/20 to-blue-500/20 relative">
                        <ImageWithFallback 
                          src={event.image_url} 
                          alt={event.name} 
                          className="w-full h-full object-cover" 
                          fallbackIcon={Calendar}
                        />
                        <div className="absolute top-2 right-2 bg-black/50 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-lg">
                          {formatDate(event.date)}
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="text-white font-bold">{event.name}</h3>
                        <div className="flex items-center gap-2 text-zinc-500 text-sm mt-1">
                          <MapPin size={14} />
                          <span>{event.location || event.city || 'Sin ubicación'}</span>
                        </div>
                        {event.producer && (
                          <div className="flex items-center gap-2 mt-3">
                            <div className="w-6 h-6 bg-zinc-800 rounded-full overflow-hidden">
                              {event.producer.branding?.logo_url ? (
                                <img src={event.producer.branding.logo_url} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <Building2 className="w-full h-full p-1 text-zinc-500" />
                              )}
                            </div>
                            <span className="text-zinc-400 text-sm">{event.producer.business_name}</span>
                          </div>
                        )}
                        <Button
                          size="sm"
                          className="w-full mt-4 bg-green-500/20 text-green-400 hover:bg-green-500/30"
                          onClick={() => openOfferModal(event)}
                        >
                          <Send size={14} className="mr-2" />
                          Ofrecer Servicios
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
        </>
      )}

      {/* Send Offer Modal - Professional Redesign */}
      <Dialog open={showOfferModal} onOpenChange={setShowOfferModal}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-3xl max-h-[90vh] p-0 overflow-hidden" data-testid="offer-modal">
          {/* Sticky Header */}
          <div className="sticky top-0 z-10 bg-zinc-950/95 backdrop-blur-sm border-b border-zinc-800 px-6 py-4">
            <DialogHeader>
              <DialogTitle className="text-white text-xl font-bold">Ofrecer Servicios</DialogTitle>
            </DialogHeader>
            
            {selectedEvent && (
              <div className="flex items-center gap-4 mt-4 bg-zinc-900/50 rounded-xl p-4 border border-zinc-800">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-xl overflow-hidden flex-shrink-0">
                  {selectedEvent.image_url ? (
                    <img src={selectedEvent.image_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Calendar className="w-full h-full p-3 text-green-400" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-white font-bold text-lg">{selectedEvent.name}</p>
                  <div className="flex items-center gap-4 text-sm text-zinc-400 mt-1">
                    <span className="flex items-center gap-1">
                      <Building2 size={14} />
                      {selectedEvent.producer?.business_name}
                    </span>
                    {selectedEvent.date && (
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        {new Date(selectedEvent.date).toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })}
                      </span>
                    )}
                    {selectedEvent.location && (
                      <span className="flex items-center gap-1">
                        <MapPin size={14} />
                        {selectedEvent.location}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {selectedEvent && (
            <div className="overflow-y-auto px-6 py-6 space-y-6 max-h-[calc(90vh-220px)]">
              {/* Service Packages Selection */}
              {press?.service_packages?.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <label className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                      <Package size={16} className="text-green-400" />
                      Tus Paquetes de Servicios
                    </label>
                    {offerForm.package_ids.length > 0 && (
                      <span className="text-xs bg-green-500/20 text-green-400 px-3 py-1 rounded-full font-medium">
                        {offerForm.package_ids.length} seleccionado{offerForm.package_ids.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    {press.service_packages.filter(pkg => pkg.is_active !== false).map((pkg) => {
                      const pkgId = pkg.id || pkg.name;
                      const isSelected = offerForm.package_ids.includes(pkgId);
                      
                      return (
                        <div
                          key={pkgId}
                          onClick={() => {
                            setOfferForm({
                              ...offerForm,
                              package_ids: isSelected
                                ? offerForm.package_ids.filter(p => p !== pkgId)
                                : [...offerForm.package_ids, pkgId]
                            });
                          }}
                          className={`relative flex flex-col p-4 rounded-xl border-2 transition-all cursor-pointer group ${
                            isSelected 
                              ? 'border-green-500 bg-green-500/5 ring-1 ring-green-500/20' 
                              : 'border-zinc-800 bg-zinc-900/40 hover:border-zinc-700 hover:bg-zinc-900/80'
                          }`}
                          data-testid={`offer-package-${pkgId}`}
                        >
                          {/* Selection indicator */}
                          <div className={`absolute top-3 right-3 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                            isSelected 
                              ? 'bg-green-500 border-green-500' 
                              : 'border-zinc-600 group-hover:border-zinc-500'
                          }`}>
                            {isSelected && <Check size={14} className="text-white" />}
                          </div>
                          
                          {/* Package Info */}
                          <div className="pr-8">
                            <h4 className="text-white font-bold">{pkg.name}</h4>
                            {pkg.category && (
                              <span className="text-xs text-zinc-500 capitalize">{pkg.category}</span>
                            )}
                          </div>
                          
                          {/* Price */}
                          <div className="flex items-baseline gap-1 mt-2 mb-3">
                            <span className="text-xl font-bold text-emerald-400">${pkg.price?.toLocaleString()}</span>
                            <span className="text-sm text-zinc-500">{pkg.currency || 'ARS'}</span>
                          </div>
                          
                          {/* Description */}
                          {pkg.description && (
                            <p className="text-sm text-zinc-400 line-clamp-2 mb-3">{pkg.description}</p>
                          )}
                          
                          {/* Includes */}
                          {pkg.includes?.length > 0 && (
                            <div className="space-y-1 mt-auto pt-3 border-t border-zinc-800">
                              {pkg.includes.slice(0, 3).map((item, idx) => (
                                <div key={idx} className="flex items-center gap-2 text-xs text-zinc-400">
                                  <Check size={12} className="text-green-500 flex-shrink-0" />
                                  <span>{item}</span>
                                </div>
                              ))}
                              {pkg.includes.length > 3 && (
                                <p className="text-xs text-zinc-500 ml-5">+{pkg.includes.length - 3} más...</p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Custom Price */}
              <div className="pt-4 border-t border-zinc-800">
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  <DollarSign size={14} className="inline mr-1.5 text-green-400" />
                  Precio propuesto (opcional)
                </label>
                <p className="text-zinc-500 text-xs mb-2">Si querés ofrecer un precio especial diferente al de tus paquetes</p>
                <div className="relative max-w-xs">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                  <Input
                    type="number"
                    value={offerForm.proposed_price}
                    onChange={(e) => setOfferForm({ ...offerForm, proposed_price: e.target.value })}
                    placeholder="15000"
                    className="bg-zinc-900 border-zinc-800 pl-8 focus:border-green-500"
                  />
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  <MessageSquare size={14} className="inline mr-1.5 text-green-400" />
                  Mensaje para la productora
                  {offerForm.package_ids.length === 0 && <span className="text-red-400 ml-1">*</span>}
                </label>
                <Textarea
                  value={offerForm.message}
                  onChange={(e) => setOfferForm({ ...offerForm, message: e.target.value })}
                  placeholder="Presentate y contale a la productora por qué deberían trabajar con vos. Mencioná tu experiencia, resultados anteriores, y qué podés aportar a su evento..."
                  className="bg-zinc-900 border-zinc-800 focus:border-green-500 min-h-[140px] resize-none"
                />
              </div>

              {/* Summary */}
              {offerForm.package_ids.length > 0 && (
                <div className="bg-zinc-900/50 rounded-xl p-4 border border-zinc-800">
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400 text-sm">Total de paquetes seleccionados:</span>
                    <span className="text-emerald-400 font-bold text-xl">
                      ${press?.service_packages
                        ?.filter(p => offerForm.package_ids.includes(p.id || p.name))
                        .reduce((sum, p) => sum + (p.price || 0), 0)
                        .toLocaleString()} ARS
                    </span>
                  </div>
                  {offerForm.proposed_price && (
                    <p className="text-zinc-500 text-xs mt-2">
                      * Ofreciendo precio especial de ${parseInt(offerForm.proposed_price).toLocaleString()} ARS
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Sticky Footer */}
          <div className="sticky bottom-0 bg-zinc-950/95 backdrop-blur-sm border-t border-zinc-800 px-6 py-4 flex justify-between items-center">
            <Button variant="ghost" onClick={() => setShowOfferModal(false)} className="text-zinc-400 hover:text-white">
              Cancelar
            </Button>
            <Button 
              onClick={sendOffer}
              disabled={sendingOffer || (offerForm.package_ids.length === 0 && !offerForm.message.trim())}
              className="bg-green-600 hover:bg-green-500 text-white px-6"
              data-testid="send-offer-btn"
            >
              {sendingOffer ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Enviando...
                </>
              ) : (
                <>
                  <Send size={16} className="mr-2" />
                  Enviar Oferta
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Chat Modal */}
      <Dialog open={showChatModal} onOpenChange={setShowChatModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl h-[600px] p-0">
          {selectedContractForChat && (
            <ContractChat 
              contract={selectedContractForChat}
              userType="press"
              onClose={() => setShowChatModal(false)}
              onContractUpdate={() => {
                // Refresh contracts list
                api.get('/press/contracts/press').then(res => {
                  // The contracts are managed by PackagesTab, we just need to trigger a refresh
                  // For now, just update the selected contract
                  api.get(`/press/contracts/${selectedContractForChat.id}`).then(contractRes => {
                    setSelectedContractForChat(contractRes.data);
                  }).catch(console.error);
                }).catch(console.error);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

// ============ Packages Tab Component ============
const PackagesTab = ({ press, onRefresh }) => {
  const [activeSubTab, setActiveSubTab] = useState('packages'); // 'packages', 'quotes', 'offers', 'contracts'
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [loadingOffers, setLoadingOffers] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [selectedContractForChat, setSelectedContractForChat] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    currency: 'ARS',
    includes: [],
    delivery_time: '',
    revisions: '1',
    category: 'contenido',
    platforms: [],
    is_featured: false,
    is_active: true
  });
  const [newInclude, setNewInclude] = useState('');

  // Listen for custom event from dashboard shortcut to switch to contracts
  useEffect(() => {
    const handleSwitchToContracts = () => {
      setActiveSubTab('contracts');
    };
    window.addEventListener('switchToContractsSubTab', handleSwitchToContracts);
    return () => {
      window.removeEventListener('switchToContractsSubTab', handleSwitchToContracts);
    };
  }, []);

  // Fetch ALL data on mount for accurate tab counts
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    // Fetch all in parallel for accurate badge counts
    Promise.all([
      api.get('/press/quotes/press').catch(() => ({ data: [] })),
      api.get('/press/contracts/press').catch(() => ({ data: [] })),
      api.get('/press/offers/press').catch(() => ({ data: [] }))
    ]).then(([quotesRes, contractsRes, offersRes]) => {
      setQuotes(quotesRes.data || []);
      setContracts(contractsRes.data || []);
      setOffers(offersRes.data || []);
    });
  };

  // Refetch specific data when tab changes (for freshness)
  useEffect(() => {
    if (activeSubTab === 'quotes') {
      fetchQuotes();
    } else if (activeSubTab === 'contracts') {
      fetchContracts();
    } else if (activeSubTab === 'offers') {
      fetchOffers();
    }
  }, [activeSubTab]);

  const fetchQuotes = async () => {
    setLoadingQuotes(true);
    try {
      const response = await api.get('/press/quotes/press');
      setQuotes(response.data || []);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    } finally {
      setLoadingQuotes(false);
    }
  };

  const fetchContracts = async () => {
    setLoadingContracts(true);
    try {
      const response = await api.get('/press/contracts/press');
      setContracts(response.data || []);
    } catch (error) {
      console.error('Error fetching contracts:', error);
    } finally {
      setLoadingContracts(false);
    }
  };

  const fetchOffers = async () => {
    setLoadingOffers(true);
    try {
      const response = await api.get('/press/offers/press');
      setOffers(response.data || []);
    } catch (error) {
      console.error('Error fetching offers:', error);
    } finally {
      setLoadingOffers(false);
    }
  };

  // Quote response state
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showQuoteResponseModal, setShowQuoteResponseModal] = useState(false);
  const [quoteResponse, setQuoteResponse] = useState({ quote_amount: '', message: '' });
  const [respondingToQuote, setRespondingToQuote] = useState(false);
  
  // Contract details modal state
  const [selectedContractDetails, setSelectedContractDetails] = useState(null);
  const [showContractDetailsModal, setShowContractDetailsModal] = useState(false);

  // Check if quote is package-only (simplified flow - accept/reject only)
  const isPackageOnlyQuote = (quote) => {
    const hasPackages = quote.package_ids && quote.package_ids.length > 0;
    const hasNoBudget = !quote.budget_range;
    const hasNoMessage = !quote.message || quote.message.trim() === '';
    return hasPackages && hasNoBudget && hasNoMessage;
  };

  // Calculate package total for display
  const calculatePackageTotal = (quote) => {
    if (!quote.package_ids || !press?.service_packages) return 0;
    let total = 0;
    for (const pkgId of quote.package_ids) {
      const pkg = press.service_packages.find(p => p.id === pkgId || p.name === pkgId);
      if (pkg) total += pkg.price || 0;
    }
    return total;
  };

  // Get package names from IDs
  const getPackageNames = (quote) => {
    if (!quote.package_ids || !press?.service_packages) return [];
    return quote.package_ids.map(pkgId => {
      const pkg = press.service_packages.find(p => p.id === pkgId || p.name === pkgId);
      return pkg?.name || pkgId;
    }).filter(Boolean);
  };

  // Accept package-only quote (uses package prices directly)
  const acceptPackageQuote = async (quote) => {
    try {
      setRespondingToQuote(true);
      const response = await api.post(`/press/quotes/${quote.id}/accept-package`);
      toast.success(`Cotización aceptada por $${response.data.quote_amount?.toLocaleString() || calculatePackageTotal(quote).toLocaleString()}`);
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aceptar la cotización');
    } finally {
      setRespondingToQuote(false);
    }
  };

  // Reject package-only quote
  const rejectPackageQuote = async (quote) => {
    try {
      setRespondingToQuote(true);
      await api.post(`/press/quotes/${quote.id}/reject-package`);
      toast.success('Cotización rechazada');
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al rechazar la cotización');
    } finally {
      setRespondingToQuote(false);
    }
  };

  // Open quote response modal for complex quotes (with message/budget)
  const openQuoteResponseModal = (quote) => {
    setSelectedQuote(quote);
    const total = calculatePackageTotal(quote);
    setQuoteResponse({ 
      quote_amount: total > 0 ? total.toString() : '', 
      message: '' 
    });
    setShowQuoteResponseModal(true);
  };

  // Submit quote response
  const submitQuoteResponse = async () => {
    if (!selectedQuote) return;
    if (!quoteResponse.quote_amount || !quoteResponse.message.trim()) {
      toast.error('Completá el monto y el mensaje');
      return;
    }
    try {
      setRespondingToQuote(true);
      await api.post(`/press/quotes/${selectedQuote.id}/respond`, {
        quote_amount: parseFloat(quoteResponse.quote_amount),
        message: quoteResponse.message
      });
      toast.success('Cotización enviada');
      setShowQuoteResponseModal(false);
      setSelectedQuote(null);
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar cotización');
    } finally {
      setRespondingToQuote(false);
    }
  };

  const PACKAGE_CATEGORIES = [
    { value: 'contenido', label: 'Contenido / Posts' },
    { value: 'cobertura', label: 'Cobertura de Evento' },
    { value: 'publicidad', label: 'Publicidad / Pauta' },
    { value: 'entrevista', label: 'Entrevista / Nota' },
    { value: 'video', label: 'Video / Reel' },
    { value: 'paquete', label: 'Paquete Completo' },
    { value: 'otro', label: 'Otro' }
  ];

  const PLATFORMS = [
    { value: 'instagram', label: 'Instagram' },
    { value: 'tiktok', label: 'TikTok' },
    { value: 'youtube', label: 'YouTube' },
    { value: 'twitter', label: 'Twitter/X' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'web', label: 'Sitio Web' },
    { value: 'newsletter', label: 'Newsletter' }
  ];

  const openCreateModal = () => {
    setEditingPackage(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      currency: 'ARS',
      includes: [],
      delivery_time: '',
      revisions: '1',
      category: 'contenido',
      platforms: [],
      is_featured: false,
      is_active: true
    });
    setShowModal(true);
  };

  const openEditModal = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      name: pkg.name || '',
      description: pkg.description || '',
      price: pkg.price?.toString() || '',
      currency: pkg.currency || 'ARS',
      includes: pkg.includes || [],
      delivery_time: pkg.delivery_time || '',
      revisions: pkg.revisions?.toString() || '1',
      category: pkg.category || 'contenido',
      platforms: pkg.platforms || [],
      is_featured: pkg.is_featured || false,
      is_active: pkg.is_active !== false
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.price) {
      toast.error('Completá el nombre y precio');
      return;
    }
    
    const price = parseFloat(formData.price);
    if (price < 1000) {
      toast.error('El precio mínimo es $1.000 ARS');
      return;
    }

    setSaving(true);
    try {
      const packageData = {
        name: formData.name,
        description: formData.description,
        price: price,
        currency: 'ARS',
        includes: formData.includes,
        delivery_time: formData.delivery_time,
        revisions: parseInt(formData.revisions) || 1,
        category: formData.category,
        platforms: formData.platforms,
        is_featured: formData.is_featured,
        is_active: formData.is_active
      };

      if (editingPackage) {
        await api.put(`/press/me/packages/${editingPackage.id}`, packageData);
        toast.success('Paquete actualizado');
      } else {
        await api.post('/press/me/packages', packageData);
        toast.success('Paquete creado');
      }
      
      setShowModal(false);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (pkgId) => {
    if (!confirm('¿Eliminar este paquete?')) return;
    
    setDeleting(pkgId);
    try {
      await api.delete(`/press/me/packages/${pkgId}`);
      toast.success('Paquete eliminado');
      onRefresh();
    } catch (error) {
      toast.error('Error al eliminar');
    } finally {
      setDeleting(null);
    }
  };

  const addInclude = () => {
    if (newInclude.trim()) {
      setFormData({
        ...formData,
        includes: [...formData.includes, newInclude.trim()]
      });
      setNewInclude('');
    }
  };

  const removeInclude = (index) => {
    setFormData({
      ...formData,
      includes: formData.includes.filter((_, i) => i !== index)
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-white mb-2">Mis Servicios</h1>
          <p className="text-zinc-400">Gestioná tus paquetes, cotizaciones y contratos</p>
        </div>
        {activeSubTab === 'packages' && (
          <Button 
            className="bg-blue-500 hover:bg-blue-600"
            onClick={openCreateModal}
            data-testid="new-package-btn"
          >
            <Plus size={16} className="mr-2" />
            Nuevo Paquete
          </Button>
        )}
      </div>

      {/* Subtabs with Enhanced Badges */}
      <div className="flex gap-1 border-b border-zinc-800 pb-2 overflow-x-auto">
        <button
          onClick={() => setActiveSubTab('packages')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg text-sm font-medium transition-all whitespace-nowrap ${
            activeSubTab === 'packages' 
              ? 'bg-blue-500/20 text-blue-400 border-b-2 border-blue-500' 
              : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
          }`}
        >
          <Package size={16} />
          <span>Mis Paquetes</span>
          <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold flex items-center justify-center ${
            activeSubTab === 'packages' ? 'bg-blue-500 text-white' : 'bg-zinc-700 text-zinc-300'
          }`}>
            {press?.service_packages?.length || 0}
          </span>
        </button>
        <button
          onClick={() => setActiveSubTab('quotes')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg text-sm font-medium transition-all whitespace-nowrap ${
            activeSubTab === 'quotes' 
              ? 'bg-yellow-500/20 text-yellow-400 border-b-2 border-yellow-500' 
              : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
          }`}
        >
          <FileText size={16} />
          <span>Cotizaciones</span>
          {quotes.filter(q => q.status === 'pending').length > 0 ? (
            <span className="min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold flex items-center justify-center bg-yellow-500 text-black animate-pulse">
              {quotes.filter(q => q.status === 'pending').length}
            </span>
          ) : (
            <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold flex items-center justify-center ${
              activeSubTab === 'quotes' ? 'bg-yellow-500/30 text-yellow-400' : 'bg-zinc-700 text-zinc-400'
            }`}>
              {quotes.length || 0}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveSubTab('offers')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg text-sm font-medium transition-all whitespace-nowrap ${
            activeSubTab === 'offers' 
              ? 'bg-purple-500/20 text-purple-400 border-b-2 border-purple-500' 
              : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
          }`}
        >
          <Send size={16} />
          <span>Mis Ofertas</span>
          <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold flex items-center justify-center ${
            activeSubTab === 'offers' ? 'bg-purple-500 text-white' : 'bg-zinc-700 text-zinc-300'
          }`}>
            {offers.length || 0}
          </span>
        </button>
        <button
          onClick={() => setActiveSubTab('contracts')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg text-sm font-medium transition-all whitespace-nowrap ${
            activeSubTab === 'contracts' 
              ? 'bg-green-500/20 text-green-400 border-b-2 border-green-500' 
              : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
          }`}
        >
          <CreditCard size={16} />
          <span>Contratos</span>
          {contracts.filter(c => c.status === 'active' || c.status === 'in_progress').length > 0 ? (
            <span className="min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold flex items-center justify-center bg-green-500 text-black">
              {contracts.filter(c => c.status === 'active' || c.status === 'in_progress').length}
            </span>
          ) : (
            <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold flex items-center justify-center ${
              activeSubTab === 'contracts' ? 'bg-green-500/30 text-green-400' : 'bg-zinc-700 text-zinc-400'
            }`}>
              {contracts.length || 0}
            </span>
          )}
        </button>
      </div>

      {/* Packages Subtab */}
      {activeSubTab === 'packages' && (
        <>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {press?.service_packages?.map((pkg, index) => (
          <div key={pkg.id || index} className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-white font-bold">{pkg.name}</h3>
                <p className="text-zinc-500 text-sm">{pkg.description}</p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs ${
                pkg.is_active !== false 
                  ? 'bg-green-500/20 text-green-400' 
                  : 'bg-zinc-700 text-zinc-400'
              }`}>
                {pkg.is_active !== false ? 'Activo' : 'Inactivo'}
              </span>
            </div>
            
            {pkg.includes?.length > 0 && (
              <ul className="text-zinc-400 text-sm mb-4 space-y-1">
                {pkg.includes.slice(0, 3).map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <Check size={12} className="text-green-400" />
                    {item}
                  </li>
                ))}
                {pkg.includes.length > 3 && (
                  <li className="text-zinc-500">+{pkg.includes.length - 3} más</li>
                )}
              </ul>
            )}

            <div className="flex items-end justify-between">
              <div>
                <p className="text-2xl font-black text-white">
                  ${pkg.price?.toLocaleString()}
                </p>
                <p className="text-zinc-500 text-xs">{pkg.currency || 'ARS'}</p>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-zinc-400 hover:text-white"
                  onClick={() => openEditModal(pkg)}
                >
                  <Edit2 size={14} />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-zinc-400 hover:text-red-400"
                  onClick={() => handleDelete(pkg.id)}
                  disabled={deleting === pkg.id}
                >
                  {deleting === pkg.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {(!press?.service_packages || press.service_packages.length === 0) && (
        <div className="bg-zinc-900/50 border border-zinc-800 border-dashed rounded-2xl p-12 text-center">
          <Package className="text-zinc-600 mx-auto mb-4" size={48} />
          <h3 className="text-white font-bold mb-2">No tenés paquetes configurados</h3>
          <p className="text-zinc-500 mb-4">Creá tu primer paquete de servicios para que las productoras puedan contratarte</p>
          <Button className="bg-blue-500 hover:bg-blue-600" onClick={openCreateModal}>
            Crear Primer Paquete
          </Button>
        </div>
      )}
        </>
      )}

      {/* Quotes Subtab - With Actions */}
      {activeSubTab === 'quotes' && (
        <div className="space-y-4">
          {loadingQuotes ? (
            <div className="flex justify-center py-12">
              <Loader2 className="animate-spin text-yellow-400" size={32} />
            </div>
          ) : quotes.length === 0 ? (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-12 text-center">
              <FileText className="text-zinc-600 mx-auto mb-4" size={48} />
              <h3 className="text-white font-bold mb-2">No hay cotizaciones</h3>
              <p className="text-zinc-500">Las solicitudes de productoras aparecerán aquí</p>
            </div>
          ) : (
            quotes.map((quote) => {
              const isPackageOnly = isPackageOnlyQuote(quote);
              const packageTotal = calculatePackageTotal(quote);
              const packageNames = getPackageNames(quote);
              const isPending = quote.status === 'pending';
              
              return (
                <div key={quote.id} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5" data-testid={`quote-item-${quote.id}`}>
                  {/* Header */}
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap mb-2">
                        <h3 className="text-white font-bold text-lg">{quote.producer_name}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs border ${
                          quote.status === 'pending' ? 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30' :
                          quote.status === 'accepted' ? 'text-green-400 bg-green-500/10 border-green-500/30' :
                          quote.status === 'rejected' ? 'text-red-400 bg-red-500/10 border-red-500/30' :
                          quote.status === 'quoted' ? 'text-blue-400 bg-blue-500/10 border-blue-500/30' :
                          quote.status === 'paid' ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' :
                          'text-zinc-400 bg-zinc-500/10 border-zinc-500/30'
                        }`}>
                          {quote.status === 'pending' ? 'Pendiente' :
                           quote.status === 'accepted' ? 'Aceptada' :
                           quote.status === 'rejected' ? 'Rechazada' :
                           quote.status === 'quoted' ? 'Cotizada' :
                           quote.status === 'paid' ? 'Pagada' :
                           quote.status}
                        </span>
                        {isPackageOnly && isPending && (
                          <span className="px-2 py-1 rounded-full text-xs bg-blue-500/10 text-blue-400 border border-blue-500/30">
                            Paquete directo
                          </span>
                        )}
                      </div>
                      {quote.event_name && (
                        <a 
                          href={`/evento/${quote.event_slug || quote.event_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1 hover:underline"
                        >
                          <Calendar size={14} />
                          Evento: {quote.event_name}
                          <ExternalLink size={12} />
                        </a>
                      )}
                      <p className="text-zinc-600 text-xs mt-1">
                        {new Date(quote.created_at).toLocaleDateString('es-AR', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    </div>
                    
                    {/* Price Display */}
                    <div className="text-right">
                      {quote.response?.quote_amount ? (
                        <div>
                          <p className="text-zinc-500 text-xs">Cotizado</p>
                          <p className="text-emerald-400 font-bold text-xl">${quote.response.quote_amount.toLocaleString()}</p>
                        </div>
                      ) : packageTotal > 0 ? (
                        <div>
                          <p className="text-zinc-500 text-xs">Total paquetes</p>
                          <p className="text-emerald-400 font-bold text-xl">${packageTotal.toLocaleString()}</p>
                        </div>
                      ) : quote.budget_range && (
                        <div>
                          <p className="text-zinc-500 text-xs">Presupuesto</p>
                          <p className="text-zinc-300 font-medium">{quote.budget_range}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Packages Selected */}
                  {packageNames.length > 0 && (
                    <div className="mb-4 p-3 bg-zinc-800/50 rounded-lg border border-zinc-700/50">
                      <p className="text-zinc-400 text-xs mb-2 flex items-center gap-1">
                        <Package size={12} />
                        Paquetes solicitados:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {packageNames.map((name, idx) => (
                          <span key={idx} className="px-2 py-1 bg-blue-500/10 text-blue-400 rounded-lg text-sm border border-blue-500/20">
                            {name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Message from Producer */}
                  {quote.message && (
                    <div className="mb-4 p-3 bg-zinc-800/30 rounded-lg border-l-2 border-zinc-600">
                      <p className="text-zinc-400 text-xs mb-1 flex items-center gap-1">
                        <MessageSquare size={12} />
                        Mensaje del productor:
                      </p>
                      <p className="text-zinc-300 text-sm">{quote.message}</p>
                    </div>
                  )}

                  {/* Actions for Pending Quotes */}
                  {isPending && (
                    <div className="pt-4 border-t border-zinc-800">
                      {isPackageOnly ? (
                        // Package-only flow: Accept or Reject directly
                        <div className="flex flex-col sm:flex-row gap-3">
                          <p className="text-zinc-500 text-sm flex-1">
                            El productor seleccionó paquetes. Podés aceptar o rechazar directamente.
                          </p>
                          <div className="flex gap-2">
                            <Button
                              onClick={() => rejectPackageQuote(quote)}
                              disabled={respondingToQuote}
                              variant="outline"
                              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                              data-testid={`reject-quote-${quote.id}`}
                            >
                              <X size={16} className="mr-1" />
                              Rechazar
                            </Button>
                            <Button
                              onClick={() => acceptPackageQuote(quote)}
                              disabled={respondingToQuote}
                              className="bg-green-600 hover:bg-green-500 text-white"
                              data-testid={`accept-quote-${quote.id}`}
                            >
                              {respondingToQuote ? (
                                <Loader2 className="animate-spin mr-1" size={16} />
                              ) : (
                                <Check size={16} className="mr-1" />
                              )}
                              Aceptar ${packageTotal.toLocaleString()}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        // Complex flow: Needs quote response with price and message
                        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                          <p className="text-zinc-500 text-sm flex-1">
                            El productor envió una consulta. Respondé con tu cotización.
                          </p>
                          <Button
                            onClick={() => openQuoteResponseModal(quote)}
                            className="bg-blue-600 hover:bg-blue-500 text-white"
                            data-testid={`respond-quote-${quote.id}`}
                          >
                            <Send size={16} className="mr-1" />
                            Cotizar Servicio
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Response Info (if already quoted) */}
                  {quote.status === 'quoted' && quote.response?.message && (
                    <div className="pt-4 border-t border-zinc-800">
                      <p className="text-zinc-400 text-xs mb-1">Tu respuesta:</p>
                      <p className="text-zinc-300 text-sm">{quote.response.message}</p>
                      <p className="text-zinc-500 text-xs mt-2">Esperando respuesta del productor...</p>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Quote Response Modal */}
      <Dialog open={showQuoteResponseModal} onOpenChange={setShowQuoteResponseModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Cotizar Servicio</DialogTitle>
          </DialogHeader>
          
          {selectedQuote && (
            <div className="space-y-4 py-4">
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <p className="text-zinc-400 text-sm">Solicitud de</p>
                <p className="text-white font-bold">{selectedQuote.producer_name}</p>
                {selectedQuote.message && (
                  <p className="text-zinc-400 text-sm mt-2">"{selectedQuote.message}"</p>
                )}
              </div>

              {getPackageNames(selectedQuote).length > 0 && (
                <div>
                  <p className="text-zinc-400 text-sm mb-2">Paquetes de interés:</p>
                  <div className="flex flex-wrap gap-2">
                    {getPackageNames(selectedQuote).map((name, idx) => (
                      <span key={idx} className="px-2 py-1 bg-blue-500/10 text-blue-400 rounded text-sm">
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm text-zinc-400 mb-1">Monto de la cotización *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                  <Input
                    type="number"
                    value={quoteResponse.quote_amount}
                    onChange={(e) => setQuoteResponse({ ...quoteResponse, quote_amount: e.target.value })}
                    placeholder="15000"
                    className="bg-zinc-800 border-zinc-700 pl-8"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">Mensaje para el productor *</label>
                <Textarea
                  value={quoteResponse.message}
                  onChange={(e) => setQuoteResponse({ ...quoteResponse, message: e.target.value })}
                  placeholder="Detallá qué incluye tu servicio, tiempos de entrega, condiciones..."
                  className="bg-zinc-800 border-zinc-700 min-h-[100px]"
                />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowQuoteResponseModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={submitQuoteResponse}
              disabled={respondingToQuote || !quoteResponse.quote_amount || !quoteResponse.message.trim()}
              className="bg-blue-500 hover:bg-blue-600"
            >
              {respondingToQuote ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Enviando...
                </>
              ) : (
                <>
                  <Send size={16} className="mr-2" />
                  Enviar Cotización
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Offers Subtab */}
      {activeSubTab === 'offers' && (
        <div className="space-y-4">
          {loadingOffers ? (
            <div className="flex justify-center py-12">
              <Loader2 className="animate-spin text-purple-400" size={32} />
            </div>
          ) : offers.length === 0 ? (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-12 text-center">
              <Send className="text-zinc-600 mx-auto mb-4" size={48} />
              <h3 className="text-white font-bold mb-2">No enviaste ofertas todavía</h3>
              <p className="text-zinc-500">Explorá eventos disponibles y ofrecé tus servicios</p>
            </div>
          ) : (
            offers.map((offer) => (
              <div key={offer.id} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="text-white font-bold">{offer.producer_name}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs border ${
                        offer.status === 'pending' ? 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30' :
                        offer.status === 'accepted' ? 'text-green-400 bg-green-500/10 border-green-500/30' :
                        offer.status === 'rejected' ? 'text-red-400 bg-red-500/10 border-red-500/30' :
                        'text-zinc-400 bg-zinc-500/10 border-zinc-500/30'
                      }`}>
                        {offer.status === 'pending' ? 'Pendiente' :
                         offer.status === 'accepted' ? 'Aceptada' :
                         offer.status === 'rejected' ? 'Rechazada' :
                         offer.status}
                      </span>
                    </div>
                    {offer.event_name && <p className="text-zinc-400 text-sm">Evento: {offer.event_name}</p>}
                    <p className="text-zinc-500 text-sm mt-1">{offer.message}</p>
                    <p className="text-zinc-600 text-xs mt-1">
                      {new Date(offer.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    {offer.proposed_price && (
                      <p className="text-purple-400 font-bold text-lg">${offer.proposed_price.toLocaleString()}</p>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Contracts Subtab */}
      {activeSubTab === 'contracts' && (
        <div className="space-y-4">
          {loadingContracts ? (
            <div className="flex justify-center py-12">
              <Loader2 className="animate-spin text-green-400" size={32} />
            </div>
          ) : contracts.length === 0 ? (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-12 text-center">
              <CreditCard className="text-zinc-600 mx-auto mb-4" size={48} />
              <h3 className="text-white font-bold mb-2">No hay contratos</h3>
              <p className="text-zinc-500">Tus contratos activos aparecerán aquí</p>
            </div>
          ) : (
            contracts.map((contract) => (
              <div key={contract.id} className={`bg-zinc-900/50 border rounded-xl p-5 ${
                contract.status === 'active' ? 'border-green-500/30' :
                contract.status === 'completed' ? 'border-emerald-500/30' :
                contract.status === 'in_progress' ? 'border-blue-500/30' :
                'border-zinc-800'
              }`} data-testid={`contract-item-${contract.id}`}>
                {/* Header */}
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap mb-2">
                      <h3 className="text-white font-bold text-lg">{contract.producer_name}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs border ${
                        contract.status === 'active' ? 'text-green-400 bg-green-500/10 border-green-500/30' :
                        contract.status === 'completed' ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' :
                        contract.status === 'in_progress' ? 'text-blue-400 bg-blue-500/10 border-blue-500/30' :
                        contract.status === 'pending_payment' ? 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30' :
                        'text-zinc-400 bg-zinc-500/10 border-zinc-500/30'
                      }`}>
                        {contract.status === 'active' ? 'Activo' :
                         contract.status === 'completed' ? 'Completado' :
                         contract.status === 'in_progress' ? 'En Progreso' :
                         contract.status === 'pending_payment' ? 'Pendiente de Pago' :
                         contract.status}
                      </span>
                    </div>
                    {contract.event_name && (
                      <p className="text-zinc-400 text-sm flex items-center gap-1">
                        <Calendar size={14} />
                        Evento: {contract.event_name}
                      </p>
                    )}
                    <p className="text-zinc-600 text-xs mt-1">
                      Contrato #{contract.id?.slice(-8)} • {new Date(contract.created_at).toLocaleDateString('es-AR', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                  
                  {/* Amount */}
                  <div className="text-right">
                    <p className="text-white font-bold text-xl">${contract.total_amount?.toLocaleString()}</p>
                    <p className="text-emerald-400 text-sm">Tu ganancia: ${contract.press_amount?.toLocaleString()}</p>
                  </div>
                </div>

                {/* Service Details - What was contracted */}
                <div className="mb-4 p-4 bg-zinc-800/50 rounded-xl border border-zinc-700/50">
                  <p className="text-zinc-400 text-xs mb-3 flex items-center gap-1 font-medium uppercase tracking-wider">
                    <Package size={12} />
                    Servicio Contratado
                  </p>
                  
                  {/* Packages */}
                  {contract.package_ids?.length > 0 ? (
                    <div className="mb-3">
                      <div className="flex flex-wrap gap-2">
                        {contract.package_ids.map((pkgId, idx) => {
                          const pkg = press?.service_packages?.find(p => p.id === pkgId || p.name === pkgId);
                          return (
                            <div key={idx} className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 flex-1 min-w-[200px]">
                              <p className="text-blue-400 font-medium">{pkg?.name || pkgId}</p>
                              {pkg?.description && (
                                <p className="text-zinc-500 text-xs mt-1 line-clamp-2">{pkg.description}</p>
                              )}
                              {pkg?.price && (
                                <p className="text-emerald-400 font-bold mt-2">${pkg.price.toLocaleString()} ARS</p>
                              )}
                              {pkg?.includes?.length > 0 && (
                                <div className="mt-2 space-y-1">
                                  {pkg.includes.slice(0, 3).map((item, i) => (
                                    <p key={i} className="text-zinc-400 text-xs flex items-center gap-1">
                                      <Check size={10} className="text-green-400" />
                                      {item}
                                    </p>
                                  ))}
                                  {pkg.includes.length > 3 && (
                                    <p className="text-zinc-500 text-xs">+{pkg.includes.length - 3} más...</p>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    /* No packages - show amount info */
                    <div className="flex items-center gap-4 p-3 bg-zinc-900/50 rounded-lg">
                      <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                        <DollarSign size={20} className="text-emerald-400" />
                      </div>
                      <div>
                        <p className="text-white font-bold">${(contract.quote_amount || contract.total_amount)?.toLocaleString()} ARS</p>
                        <p className="text-zinc-500 text-xs">Servicio de difusión personalizado</p>
                      </div>
                    </div>
                  )}

                  {/* Quote message if exists */}
                  {contract.quote_message && (
                    <div className="mt-3 pt-3 border-t border-zinc-700/50">
                      <p className="text-zinc-500 text-xs mb-1">Mensaje del productor:</p>
                      <p className="text-zinc-300 text-sm">{contract.quote_message}</p>
                    </div>
                  )}

                  {/* Response message if exists */}
                  {contract.response_message && (
                    <div className="mt-3 pt-3 border-t border-zinc-700/50">
                      <p className="text-zinc-500 text-xs mb-1">Tu respuesta:</p>
                      <p className="text-zinc-300 text-sm">{contract.response_message}</p>
                    </div>
                  )}
                  
                  {/* Budget range if exists */}
                  {contract.budget_range && !contract.package_ids?.length && (
                    <div className="mt-3 pt-3 border-t border-zinc-700/50">
                      <p className="text-zinc-500 text-xs">Presupuesto indicado: <span className="text-zinc-300">${contract.budget_range}</span></p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2 pt-3 border-t border-zinc-800">
                  {/* Chat Button - Only available when work is in progress */}
                  {contract.status === 'in_progress' && (
                    <Button
                      onClick={() => {
                        setSelectedContractForChat(contract);
                        setShowChatModal(true);
                      }}
                      className="bg-blue-600 hover:bg-blue-500 text-white"
                      data-testid={`chat-contract-${contract.id}`}
                    >
                      <MessageSquare size={16} className="mr-2" />
                      Abrir Chat
                    </Button>
                  )}

                  {/* Info for active contracts - chat not available yet */}
                  {contract.status === 'active' && (
                    <div className="text-zinc-400 text-sm flex items-center gap-2 bg-zinc-800/50 px-3 py-2 rounded-lg">
                      <MessageSquare size={14} className="text-zinc-500" />
                      Chat disponible cuando inicie el trabajo
                    </div>
                  )}

                  {/* View Details */}
                  <Button
                    variant="outline"
                    className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                    onClick={() => {
                      setSelectedContractDetails(contract);
                      setShowContractDetailsModal(true);
                    }}
                    data-testid={`details-contract-${contract.id}`}
                  >
                    <Eye size={16} className="mr-2" />
                    Ver Detalles
                  </Button>

                  {/* Completed - show chat for history */}
                  {contract.status === 'completed' && (
                    <>
                      <Button
                        onClick={() => {
                          setSelectedContractForChat(contract);
                          setShowChatModal(true);
                        }}
                        variant="outline"
                        className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                      >
                        <MessageSquare size={16} className="mr-2" />
                        Ver Historial
                      </Button>
                      <div className="flex items-center gap-2 text-emerald-400 text-sm ml-auto">
                        <CheckCircle size={16} />
                        Trabajo completado
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Chat Modal for Contracts */}
      <Dialog open={showChatModal} onOpenChange={setShowChatModal}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-4xl h-[85vh] p-0 overflow-hidden">
          {selectedContractForChat && (
            <ContractChat
              contract={selectedContractForChat}
              userType="press"
              onClose={() => {
                setShowChatModal(false);
                setSelectedContractForChat(null);
                fetchContracts(); // Refresh contracts after chat closes
              }}
              onContractUpdate={() => {
                fetchContracts();
                // Also refresh the selected contract
                api.get(`/press/contracts/${selectedContractForChat.id}`).then(res => {
                  setSelectedContractForChat(res.data);
                }).catch(console.error);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Contract Details Modal */}
      <Dialog open={showContractDetailsModal} onOpenChange={setShowContractDetailsModal}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-xl flex items-center gap-2">
              <FileText size={20} className="text-blue-400" />
              Detalles del Contrato
            </DialogTitle>
          </DialogHeader>
          
          {selectedContractDetails && (
            <div className="space-y-6 py-4">
              {/* Contract Header */}
              <div className="flex items-center justify-between p-4 bg-zinc-900/50 rounded-xl border border-zinc-800">
                <div>
                  <p className="text-zinc-500 text-xs">Contrato</p>
                  <p className="text-white font-mono text-sm">#{selectedContractDetails.id}</p>
                </div>
                <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                  selectedContractDetails.status === 'active' ? 'bg-green-500/20 text-green-400' :
                  selectedContractDetails.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                  selectedContractDetails.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' :
                  selectedContractDetails.status === 'pending_payment' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-zinc-500/20 text-zinc-400'
                }`}>
                  {selectedContractDetails.status === 'active' ? 'Activo' :
                   selectedContractDetails.status === 'completed' ? 'Completado' :
                   selectedContractDetails.status === 'in_progress' ? 'En Progreso' :
                   selectedContractDetails.status === 'pending_payment' ? 'Pendiente de Pago' :
                   selectedContractDetails.status}
                </span>
              </div>

              {/* Parties */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-zinc-900/30 rounded-xl border border-zinc-800">
                  <p className="text-zinc-500 text-xs mb-2 flex items-center gap-1">
                    <Building2 size={12} />
                    PRODUCTORA
                  </p>
                  <p className="text-white font-bold">{selectedContractDetails.producer_name}</p>
                </div>
                <div className="p-4 bg-zinc-900/30 rounded-xl border border-zinc-800">
                  <p className="text-zinc-500 text-xs mb-2 flex items-center gap-1">
                    <Newspaper size={12} />
                    MEDIO DE PRENSA
                  </p>
                  <p className="text-white font-bold">{press?.media_name || 'Tu medio'}</p>
                </div>
              </div>

              {/* Financial Details */}
              <div className="p-4 bg-gradient-to-br from-emerald-500/5 to-green-500/5 rounded-xl border border-emerald-500/20">
                <p className="text-emerald-400 text-xs mb-3 font-medium uppercase tracking-wider">Detalles Financieros</p>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Monto cotizado:</span>
                    <span className="text-white font-medium">${selectedContractDetails.quote_amount?.toLocaleString() || '-'} ARS</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Total del contrato:</span>
                    <span className="text-white font-medium">${selectedContractDetails.total_amount?.toLocaleString() || '-'} ARS</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Costo servicio plataforma ({selectedContractDetails.platform_fee_percent || 10}%):</span>
                    <span className="text-zinc-400">${selectedContractDetails.platform_fee?.toLocaleString() || '-'} ARS</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-zinc-700">
                    <span className="text-emerald-400 font-medium">Tu ganancia neta:</span>
                    <span className="text-emerald-400 font-bold text-lg">${selectedContractDetails.press_amount?.toLocaleString() || '-'} ARS</span>
                  </div>
                </div>
              </div>

              {/* Service Details */}
              <div>
                <p className="text-zinc-400 text-xs mb-3 font-medium uppercase tracking-wider flex items-center gap-1">
                  <Package size={12} />
                  Servicio Contratado
                </p>
                
                {selectedContractDetails.package_ids?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedContractDetails.package_ids.map((pkgId, idx) => {
                      const pkg = press?.service_packages?.find(p => p.id === pkgId || p.name === pkgId);
                      return (
                        <div key={idx} className="p-4 bg-zinc-900/50 rounded-xl border border-zinc-800">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-blue-400 font-bold">{pkg?.name || pkgId}</p>
                            {pkg?.price && <p className="text-emerald-400 font-bold">${pkg.price.toLocaleString()} ARS</p>}
                          </div>
                          {pkg?.description && <p className="text-zinc-400 text-sm mb-3">{pkg.description}</p>}
                          {pkg?.includes?.length > 0 && (
                            <div className="space-y-1">
                              <p className="text-zinc-500 text-xs">Incluye:</p>
                              {pkg.includes.map((item, i) => (
                                <p key={i} className="text-zinc-300 text-sm flex items-center gap-2">
                                  <Check size={14} className="text-green-400" />
                                  {item}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-4 bg-zinc-900/50 rounded-xl border border-zinc-800">
                    <p className="text-zinc-300">Servicio de difusión personalizado</p>
                    <p className="text-emerald-400 font-bold mt-2">${selectedContractDetails.quote_amount?.toLocaleString() || selectedContractDetails.total_amount?.toLocaleString()} ARS</p>
                  </div>
                )}
              </div>

              {/* Messages */}
              {(selectedContractDetails.quote_message || selectedContractDetails.response_message) && (
                <div>
                  <p className="text-zinc-400 text-xs mb-3 font-medium uppercase tracking-wider flex items-center gap-1">
                    <MessageSquare size={12} />
                    Comunicación
                  </p>
                  
                  {selectedContractDetails.quote_message && (
                    <div className="p-4 bg-zinc-900/50 rounded-xl border border-zinc-800 mb-3">
                      <p className="text-zinc-500 text-xs mb-1">Solicitud del productor:</p>
                      <p className="text-zinc-300">{selectedContractDetails.quote_message}</p>
                    </div>
                  )}
                  
                  {selectedContractDetails.response_message && (
                    <div className="p-4 bg-zinc-900/50 rounded-xl border border-zinc-800">
                      <p className="text-zinc-500 text-xs mb-1">Tu respuesta:</p>
                      <p className="text-zinc-300">{selectedContractDetails.response_message}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Dates */}
              <div className="flex items-center justify-between p-4 bg-zinc-900/30 rounded-xl border border-zinc-800 text-sm">
                <div>
                  <p className="text-zinc-500 text-xs">Fecha de creación</p>
                  <p className="text-zinc-300">{new Date(selectedContractDetails.created_at).toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                </div>
                {selectedContractDetails.event_name && (
                  <div className="text-right">
                    <p className="text-zinc-500 text-xs">Evento</p>
                    <p className="text-zinc-300">{selectedContractDetails.event_name}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end pt-4 border-t border-zinc-800">
            <Button onClick={() => setShowContractDetailsModal(false)} className="bg-zinc-800 hover:bg-zinc-700">
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Package Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">
              {editingPackage ? 'Editar Paquete' : 'Nuevo Paquete de Servicios'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-5 py-4">
            {/* Basic Info */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-zinc-400 flex items-center gap-2">
                <Package size={16} />
                Información Básica
              </h3>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label className="text-zinc-400">Nombre del paquete *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ej: Cobertura Completa + Reel"
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-zinc-400">Categoría</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(v) => setFormData({ ...formData, category: v })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {PACKAGE_CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-zinc-400">Precio * <span className="text-zinc-500 text-xs">(mínimo $1.000)</span></Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="number"
                      min="1000"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      placeholder="1000"
                      className="bg-zinc-800 border-zinc-700 flex-1"
                    />
                    <div className="bg-zinc-700 border border-zinc-600 rounded-md px-4 flex items-center text-zinc-300 font-medium">
                      ARS
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-zinc-400">Descripción</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe detalladamente qué incluye tu paquete y qué puede esperar el cliente..."
                  className="bg-zinc-800 border-zinc-700 mt-1 min-h-[80px]"
                />
              </div>
            </div>

            {/* Platforms */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-zinc-400 flex items-center gap-2">
                <Globe size={16} />
                Plataformas
              </h3>
              <div className="flex flex-wrap gap-2">
                {PLATFORMS.map(platform => (
                  <label 
                    key={platform.value}
                    className={`px-3 py-2 rounded-lg border cursor-pointer transition-all ${
                      formData.platforms.includes(platform.value)
                        ? 'bg-blue-500/20 border-blue-500 text-blue-400'
                        : 'bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={formData.platforms.includes(platform.value)}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          platforms: e.target.checked
                            ? [...formData.platforms, platform.value]
                            : formData.platforms.filter(p => p !== platform.value)
                        });
                      }}
                    />
                    {platform.label}
                  </label>
                ))}
              </div>
            </div>

            {/* What's Included */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-zinc-400 flex items-center gap-2">
                <Check size={16} />
                ¿Qué incluye?
              </h3>
              <div className="flex gap-2">
                <Input
                  value={newInclude}
                  onChange={(e) => setNewInclude(e.target.value)}
                  placeholder="Ej: 1 post en feed, 3 stories, 1 reel..."
                  className="bg-zinc-800 border-zinc-700"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addInclude())}
                />
                <Button type="button" onClick={addInclude} variant="outline" className="px-3">
                  <Plus size={16} />
                </Button>
              </div>
              {formData.includes.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.includes.map((item, index) => (
                    <span
                      key={index}
                      className="bg-green-500/10 text-green-400 border border-green-500/30 px-3 py-1.5 rounded-lg text-sm flex items-center gap-2"
                    >
                      <Check size={12} />
                      {item}
                      <button
                        onClick={() => removeInclude(index)}
                        className="text-green-400/60 hover:text-red-400 ml-1"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <p className="text-zinc-500 text-xs">
                Tip: Sé específico. Ej: "3 stories con swipe up", "Reel de 30seg con música"
              </p>
            </div>

            {/* Delivery Details */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-zinc-400 flex items-center gap-2">
                <Clock size={16} />
                Entrega
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400">Tiempo de entrega</Label>
                  <Select 
                    value={formData.delivery_time} 
                    onValueChange={(v) => setFormData({ ...formData, delivery_time: v })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="24hs">24 horas</SelectItem>
                      <SelectItem value="48hs">48 horas</SelectItem>
                      <SelectItem value="72hs">72 horas</SelectItem>
                      <SelectItem value="1 semana">1 semana</SelectItem>
                      <SelectItem value="2 semanas">2 semanas</SelectItem>
                      <SelectItem value="A coordinar">A coordinar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-400">Revisiones incluidas</Label>
                  <Select 
                    value={formData.revisions} 
                    onValueChange={(v) => setFormData({ ...formData, revisions: v })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="0">Sin revisiones</SelectItem>
                      <SelectItem value="1">1 revisión</SelectItem>
                      <SelectItem value="2">2 revisiones</SelectItem>
                      <SelectItem value="3">3 revisiones</SelectItem>
                      <SelectItem value="unlimited">Ilimitadas</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Options */}
            <div className="space-y-3 pt-2 border-t border-zinc-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_featured"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                    className="rounded bg-zinc-800 border-zinc-700"
                  />
                  <Label htmlFor="is_featured" className="text-zinc-400 cursor-pointer">
                    Destacar este paquete
                  </Label>
                </div>
                <span className="text-xs text-zinc-500">Aparecerá primero en tu perfil</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="rounded bg-zinc-800 border-zinc-700"
                  />
                  <Label htmlFor="is_active" className="text-zinc-400 cursor-pointer">
                    Paquete activo
                  </Label>
                </div>
                <span className="text-xs text-zinc-500">Visible para productoras</span>
              </div>
            </div>
          </div>

          <DialogFooter className="border-t border-zinc-800 pt-4">
            <Button variant="ghost" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-500 hover:bg-blue-600"
            >
              {saving ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Guardando...
                </>
              ) : (
                <>
                  <Save size={16} className="mr-2" />
                  {editingPackage ? 'Guardar Cambios' : 'Crear Paquete'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

// ============ Portfolio Tab Component ============
const PortfolioTab = ({ press, onRefresh }) => {
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    event_name: '',
    producer_name: '',
    date: '',
    work_url: '',
    platform: '',
    content_type: '',
    metrics: { reach: '', likes: '', comments: '', shares: '', views: '' }
  });

  const openCreateModal = () => {
    setEditingItem(null);
    setFormData({
      title: '',
      description: '',
      image_url: '',
      event_name: '',
      producer_name: '',
      date: '',
      work_url: '',
      platform: '',
      content_type: '',
      metrics: { reach: '', likes: '', comments: '', shares: '', views: '' }
    });
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title || '',
      description: item.description || '',
      image_url: item.image_url || '',
      event_name: item.event_name || '',
      producer_name: item.producer_name || '',
      date: item.date || '',
      work_url: item.work_url || '',
      platform: item.platform || '',
      content_type: item.content_type || '',
      metrics: {
        reach: item.metrics?.reach?.toString() || '',
        likes: item.metrics?.likes?.toString() || '',
        comments: item.metrics?.comments?.toString() || '',
        shares: item.metrics?.shares?.toString() || '',
        views: item.metrics?.views?.toString() || ''
      }
    });
    setShowModal(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten imágenes');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('La imagen no puede superar 10MB');
      return;
    }

    setUploadingImage(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await api.post('/upload/image', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFormData({ ...formData, image_url: response.data.url });
      toast.success('Imagen subida correctamente');
    } catch (error) {
      toast.error('Error al subir la imagen');
      console.error(error);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Completá el título del trabajo');
      return;
    }

    setSaving(true);
    try {
      const itemData = {
        title: formData.title,
        description: formData.description,
        image_url: formData.image_url,
        event_name: formData.event_name,
        producer_name: formData.producer_name,
        date: formData.date,
        work_url: formData.work_url,
        platform: formData.platform,
        content_type: formData.content_type,
        metrics: {
          reach: formData.metrics.reach ? parseInt(formData.metrics.reach) : null,
          likes: formData.metrics.likes ? parseInt(formData.metrics.likes) : null,
          comments: formData.metrics.comments ? parseInt(formData.metrics.comments) : null,
          shares: formData.metrics.shares ? parseInt(formData.metrics.shares) : null,
          views: formData.metrics.views ? parseInt(formData.metrics.views) : null
        }
      };

      if (editingItem) {
        // Update existing item
        await api.put(`/press/me/portfolio/${editingItem.id}`, itemData);
        toast.success('Trabajo actualizado');
      } else {
        // Create new item
        await api.post('/press/me/portfolio', itemData);
        toast.success('Trabajo agregado al portfolio');
      }
      
      setShowModal(false);
      setEditingItem(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (itemId) => {
    if (!confirm('¿Eliminar este trabajo del portfolio?')) return;
    
    setDeleting(itemId);
    try {
      await api.delete(`/press/me/portfolio/${itemId}`);
      toast.success('Trabajo eliminado');
      onRefresh();
    } catch (error) {
      toast.error('Error al eliminar');
    } finally {
      setDeleting(null);
    }
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const platformColors = {
    instagram: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
    tiktok: 'bg-zinc-500/20 text-zinc-300 border-zinc-500/30',
    youtube: 'bg-red-500/20 text-red-400 border-red-500/30',
    twitter: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    facebook: 'bg-blue-600/20 text-blue-400 border-blue-600/30',
    web: 'bg-green-500/20 text-green-400 border-green-500/30'
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-white mb-2">Mi Portfolio</h1>
          <p className="text-zinc-400">Mostrá tus mejores trabajos a productoras</p>
        </div>
        <Button 
          className="bg-purple-500 hover:bg-purple-600"
          onClick={openCreateModal}
          data-testid="add-portfolio-btn"
        >
          <Plus size={16} className="mr-2" />
          Agregar Trabajo
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {press?.portfolio?.map((item, index) => (
          <div key={item.id || index} className="bg-zinc-900/50 border border-zinc-800 rounded-2xl overflow-hidden group relative">
            <div className="aspect-video bg-zinc-800 relative">
              <ImageWithFallback 
                src={item.image_url} 
                alt={item.title} 
                className="w-full h-full object-cover" 
                fallbackIcon={Camera}
              />
              {item.platform && (
                <span className={`absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-medium border ${platformColors[item.platform] || platformColors.web}`}>
                  {item.platform}
                </span>
              )}
              <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => openEditModal(item)}
                  className="bg-yellow-500/80 hover:bg-yellow-500 text-black p-2 rounded-lg"
                  title="Editar"
                >
                  <Edit2 size={16} />
                </button>
                {item.work_url && (
                  <a
                    href={item.work_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-blue-500/80 hover:bg-blue-500 text-white p-2 rounded-lg"
                  >
                    <ExternalLink size={16} />
                  </a>
                )}
                <button
                  onClick={() => handleDelete(item.id)}
                  disabled={deleting === item.id}
                  className="bg-red-500/80 hover:bg-red-500 text-white p-2 rounded-lg"
                >
                  {deleting === item.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                </button>
              </div>
            </div>
            <div className="p-4">
              <h3 className="text-white font-bold mb-1">{item.title}</h3>
              {item.description && (
                <p className="text-zinc-500 text-sm line-clamp-2 mb-2">{item.description}</p>
              )}
              <div className="flex flex-wrap gap-2 mb-2">
                {item.event_name && (
                  <span className="text-zinc-500 text-xs bg-zinc-800 px-2 py-1 rounded">{item.event_name}</span>
                )}
                {item.producer_name && (
                  <span className="text-zinc-500 text-xs bg-zinc-800 px-2 py-1 rounded">{item.producer_name}</span>
                )}
                {item.content_type && (
                  <span className="text-purple-400 text-xs bg-purple-500/10 px-2 py-1 rounded">{item.content_type}</span>
                )}
              </div>
              {item.metrics && (
                <div className="flex flex-wrap gap-3 mt-2 text-xs text-zinc-400">
                  {item.metrics.views && <span className="flex items-center gap-1"><Eye size={12} />{formatNumber(item.metrics.views)}</span>}
                  {item.metrics.reach && <span className="flex items-center gap-1"><TrendingUp size={12} />{formatNumber(item.metrics.reach)}</span>}
                  {item.metrics.likes && <span className="flex items-center gap-1"><Heart size={12} />{formatNumber(item.metrics.likes)}</span>}
                  {item.metrics.comments && <span className="flex items-center gap-1"><MessageCircle size={12} />{formatNumber(item.metrics.comments)}</span>}
                  {item.metrics.shares && <span className="flex items-center gap-1"><Share2 size={12} />{formatNumber(item.metrics.shares)}</span>}
                </div>
              )}
              {item.work_url && (
                <a 
                  href={item.work_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-400 text-xs hover:underline flex items-center gap-1 mt-2"
                >
                  <ExternalLink size={12} /> Ver trabajo original
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      {(!press?.portfolio || press.portfolio.length === 0) && (
        <div className="bg-zinc-900/50 border border-zinc-800 border-dashed rounded-2xl p-12 text-center">
          <Image className="text-zinc-600 mx-auto mb-4" size={48} />
          <h3 className="text-white font-bold mb-2">Tu portfolio está vacío</h3>
          <p className="text-zinc-500 mb-4">Agregá ejemplos de tu trabajo para mostrar a las productoras</p>
          <Button className="bg-purple-500 hover:bg-purple-600" onClick={openCreateModal}>
            Agregar Primer Trabajo
          </Button>
        </div>
      )}

      {/* Portfolio Modal - Improved */}
      <Dialog open={showModal} onOpenChange={(open) => { setShowModal(open); if (!open) setEditingItem(null); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">
              {editingItem ? 'Editar Trabajo del Portfolio' : 'Agregar Trabajo al Portfolio'}
            </DialogTitle>
            <p className="text-zinc-500 text-sm">Mostrá tu mejor contenido para atraer productoras</p>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Image Upload Section */}
            <div className="space-y-3">
              <Label className="text-zinc-300 font-medium">Imagen del Trabajo</Label>
              <div className="flex gap-4">
                <div className="flex-1">
                  <div 
                    className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                      formData.image_url ? 'border-green-500/50 bg-green-500/5' : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                    onClick={() => document.getElementById('portfolio-image-upload').click()}
                  >
                    {uploadingImage ? (
                      <Loader2 className="mx-auto text-purple-400 animate-spin" size={32} />
                    ) : formData.image_url ? (
                      <div className="space-y-2">
                        <CheckCircle className="mx-auto text-green-400" size={32} />
                        <p className="text-green-400 text-sm">Imagen cargada</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Upload className="mx-auto text-zinc-500" size={32} />
                        <p className="text-zinc-400 text-sm">Click para subir imagen</p>
                        <p className="text-zinc-600 text-xs">PNG, JPG hasta 10MB</p>
                      </div>
                    )}
                  </div>
                  <input
                    id="portfolio-image-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </div>
                {formData.image_url && (
                  <div className="w-32 h-24 rounded-lg overflow-hidden bg-zinc-800">
                    <img src={formData.image_url} alt="Preview" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 text-zinc-500 text-xs">
                <span>O pegá una URL directa:</span>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="https://..."
                  className="bg-zinc-800 border-zinc-700 text-xs h-8 flex-1"
                />
              </div>
            </div>

            {/* Basic Info */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Título del Trabajo *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Ej: Cobertura Festival XYZ 2024"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Tipo de Contenido</Label>
                <Select 
                  value={formData.content_type || ''} 
                  onValueChange={(v) => setFormData({ ...formData, content_type: v })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="post">Post / Publicación</SelectItem>
                    <SelectItem value="reel">Reel / Video Corto</SelectItem>
                    <SelectItem value="story">Stories</SelectItem>
                    <SelectItem value="video">Video Largo</SelectItem>
                    <SelectItem value="articulo">Artículo / Nota</SelectItem>
                    <SelectItem value="galeria">Galería de Fotos</SelectItem>
                    <SelectItem value="livestream">Transmisión en Vivo</SelectItem>
                    <SelectItem value="podcast">Podcast / Audio</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-zinc-300">Descripción</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe el trabajo, qué hiciste, resultados obtenidos..."
                className="bg-zinc-800 border-zinc-700 mt-1"
                rows={3}
              />
            </div>

            {/* Event & Producer */}
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label className="text-zinc-300">Evento</Label>
                <Input
                  value={formData.event_name}
                  onChange={(e) => setFormData({ ...formData, event_name: e.target.value })}
                  placeholder="Nombre del evento"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Productora</Label>
                <Input
                  value={formData.producer_name}
                  onChange={(e) => setFormData({ ...formData, producer_name: e.target.value })}
                  placeholder="Nombre de la productora"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Fecha</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
            </div>

            {/* Platform & URL */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Plataforma</Label>
                <Select 
                  value={formData.platform || ''} 
                  onValueChange={(v) => setFormData({ ...formData, platform: v })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="instagram">Instagram</SelectItem>
                    <SelectItem value="tiktok">TikTok</SelectItem>
                    <SelectItem value="youtube">YouTube</SelectItem>
                    <SelectItem value="twitter">Twitter/X</SelectItem>
                    <SelectItem value="facebook">Facebook</SelectItem>
                    <SelectItem value="web">Web / Blog</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-300">URL del Trabajo Original</Label>
                <Input
                  value={formData.work_url}
                  onChange={(e) => setFormData({ ...formData, work_url: e.target.value })}
                  placeholder="https://instagram.com/p/..."
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
            </div>

            {/* Metrics */}
            <div>
              <Label className="text-zinc-300 font-medium mb-3 block">Métricas del Contenido</Label>
              <div className="grid grid-cols-5 gap-3">
                <div>
                  <Label className="text-zinc-500 text-xs">Vistas</Label>
                  <Input
                    type="number"
                    value={formData.metrics.views}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      metrics: { ...formData.metrics, views: e.target.value }
                    })}
                    placeholder="10K"
                    className="bg-zinc-800 border-zinc-700 mt-1 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-zinc-500 text-xs">Alcance</Label>
                  <Input
                    type="number"
                    value={formData.metrics.reach}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      metrics: { ...formData.metrics, reach: e.target.value }
                    })}
                    placeholder="5000"
                    className="bg-zinc-800 border-zinc-700 mt-1 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-zinc-500 text-xs">Likes</Label>
                  <Input
                    type="number"
                    value={formData.metrics.likes}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      metrics: { ...formData.metrics, likes: e.target.value }
                    })}
                    placeholder="200"
                    className="bg-zinc-800 border-zinc-700 mt-1 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-zinc-500 text-xs">Comentarios</Label>
                  <Input
                    type="number"
                    value={formData.metrics.comments}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      metrics: { ...formData.metrics, comments: e.target.value }
                    })}
                    placeholder="50"
                    className="bg-zinc-800 border-zinc-700 mt-1 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-zinc-500 text-xs">Compartidos</Label>
                  <Input
                    type="number"
                    value={formData.metrics.shares}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      metrics: { ...formData.metrics, shares: e.target.value }
                    })}
                    placeholder="30"
                    className="bg-zinc-800 border-zinc-700 mt-1 text-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button 
              className="bg-purple-500 hover:bg-purple-600" 
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
              Guardar Trabajo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};


// ============ Settlements Tab Component ============
const SettlementsTab = ({ press }) => {
  const [earnings, setEarnings] = useState(null);
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestAmount, setRequestAmount] = useState('');
  const [detailModal, setDetailModal] = useState({ open: false, settlement: null });

  useEffect(() => {
    fetchData();
  }, [press]);

  const fetchData = async () => {
    if (!press?.id) return;
    
    try {
      setLoading(true);
      
      // Get earnings
      const earningsRes = await api.get(`/settlements/my-earnings?entity_type=press&entity_id=${press.id}`);
      setEarnings(earningsRes.data);
      
      // Get settlements history
      const settlementsRes = await api.get(`/settlements/my-settlements?entity_type=press&entity_id=${press.id}`);
      setSettlements(settlementsRes.data.settlements || []);
    } catch (error) {
      console.error('Error fetching settlements data:', error);
      toast.error('Error al cargar datos de liquidaciones');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSettlement = async () => {
    const amount = parseFloat(requestAmount);
    if (!amount || amount <= 0) {
      toast.error('Ingresá un monto válido');
      return;
    }
    
    if (amount > (earnings?.available_balance || 0)) {
      toast.error(`El monto máximo disponible es ${formatCurrency(earnings?.available_balance)}`);
      return;
    }
    
    try {
      setRequesting(true);
      await api.post('/settlements/request', {
        entity_type: 'press',
        entity_id: press.id,
        amount: amount,
        description: 'Solicitud de liquidación - Portal Servicios'
      });
      
      toast.success('Solicitud enviada - Recibirás un email de confirmación');
      setShowRequestModal(false);
      setRequestAmount('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar solicitud');
    } finally {
      setRequesting(false);
    }
  };

  const getStatusConfig = (status) => {
    const config = {
      pending: { 
        bg: 'bg-amber-500/10', 
        border: 'border-amber-500/30', 
        text: 'text-amber-400',
        icon: Clock,
        label: 'En Revisión'
      },
      approved: { 
        bg: 'bg-blue-500/10', 
        border: 'border-blue-500/30', 
        text: 'text-blue-400',
        icon: CheckCircle,
        label: 'Aprobada'
      },
      paid: { 
        bg: 'bg-emerald-500/10', 
        border: 'border-emerald-500/30', 
        text: 'text-emerald-400',
        icon: DollarSign,
        label: 'Pagada'
      },
      rejected: { 
        bg: 'bg-red-500/10', 
        border: 'border-red-500/30', 
        text: 'text-red-400',
        icon: AlertCircle,
        label: 'Rechazada'
      }
    };
    return config[status] || config.pending;
  };

  const getStatusBadge = (status) => {
    const c = getStatusConfig(status);
    const IconComponent = c.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${c.bg} ${c.border} ${c.text}`}>
        <IconComponent size={12} />
        {c.label}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="animate-spin text-blue-500" size={32} />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
      data-testid="services-settlements-tab"
    >
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-black text-white flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center">
              <DollarSign className="text-white" size={22} />
            </div>
            Mis Liquidaciones
          </h2>
          <p className="text-zinc-400 text-sm mt-1">
            Gestiona tus cobros por servicios prestados
          </p>
        </div>
        
        {earnings?.available_balance > 0 && (
          <Button
            onClick={() => setShowRequestModal(true)}
            className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white shadow-lg shadow-emerald-500/20"
          >
            <Plus size={18} className="mr-2" />
            Solicitar Liquidación
          </Button>
        )}
      </div>

      {/* Info Banner */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 flex items-start gap-3">
        <Info className="text-blue-400 mt-0.5 flex-shrink-0" size={20} />
        <div>
          <p className="text-blue-300 font-medium text-sm">Información sobre Pagos</p>
          <p className="text-blue-200/70 text-sm mt-1">
            Los montos mostrados son <strong>ingresos netos</strong> (sin comisiones de la plataforma). 
            Una vez aprobada tu solicitud, el pago puede demorar entre <strong>24 y 48 horas hábiles</strong> en acreditarse según tu banco.
          </p>
        </div>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-zinc-800/50 to-zinc-900/50 border border-zinc-700/50 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wide">Ingreso Neto Total</p>
              <p className="text-3xl font-black text-white mt-2">{formatCurrency(earnings?.total_earned)}</p>
              <p className="text-zinc-500 text-sm mt-1">Historial completo</p>
            </div>
            <div className="w-14 h-14 bg-blue-500/20 rounded-2xl flex items-center justify-center">
              <TrendingUp className="text-blue-400" size={28} />
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-zinc-800/50 to-zinc-900/50 border border-zinc-700/50 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wide">Ya Liquidado</p>
              <p className="text-3xl font-black text-white mt-2">{formatCurrency(earnings?.total_settled)}</p>
              <p className="text-zinc-500 text-sm mt-1">Pagos recibidos</p>
            </div>
            <div className="w-14 h-14 bg-violet-500/20 rounded-2xl flex items-center justify-center">
              <CheckCircle className="text-violet-400" size={28} />
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/30 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-400 text-xs font-semibold uppercase tracking-wide">Disponible</p>
              <p className="text-3xl font-black text-emerald-400 mt-2">{formatCurrency(earnings?.available_balance)}</p>
              <p className="text-emerald-400/60 text-sm mt-1">Listo para solicitar</p>
            </div>
            <div className="w-14 h-14 bg-emerald-500/20 rounded-2xl flex items-center justify-center">
              <DollarSign className="text-emerald-400" size={28} />
            </div>
          </div>
        </div>
      </div>

      {/* Earnings by Contract */}
      {earnings?.earnings_detail?.length > 0 && (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-zinc-800 flex items-center gap-2">
            <Briefcase className="text-blue-400" size={18} />
            <h3 className="font-bold text-white">Detalle por Contrato</h3>
          </div>
          <div className="divide-y divide-zinc-800">
            {earnings.earnings_detail.map((item, index) => (
              <div key={index} className="px-6 py-4 flex items-center justify-between hover:bg-zinc-800/30 transition-colors">
                <div>
                  <p className="font-semibold text-white">{item.event_title || item.contract_title || 'Contrato'}</p>
                  <p className="text-sm text-zinc-500 mt-0.5">
                    Generado: <span className="text-zinc-300">{formatCurrency(item.total_earned)}</span>
                    {' • '}
                    Liquidado: <span className="text-zinc-300">{formatCurrency(item.already_settled)}</span>
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-emerald-400 font-bold text-lg">{formatCurrency(item.available)}</p>
                  <p className="text-xs text-zinc-500">Disponible</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Settlements History */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center gap-2">
          <Clock className="text-blue-400" size={18} />
          <h3 className="font-bold text-white">Historial de Liquidaciones</h3>
        </div>
        
        {settlements.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-zinc-500">
            <DollarSign size={48} className="mb-4 opacity-30" />
            <p className="font-medium">No tenés liquidaciones todavía</p>
            <p className="text-sm mt-1">Las solicitudes aparecerán aquí</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-800">
            {settlements.map((settlement) => {
              const statusConfig = getStatusConfig(settlement.status);
              
              return (
                <div 
                  key={settlement.id} 
                  className="px-6 py-4 flex items-center justify-between hover:bg-zinc-800/30 transition-colors cursor-pointer"
                  onClick={() => setDetailModal({ open: true, settlement })}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${statusConfig.bg}`}>
                      <statusConfig.icon className={statusConfig.text} size={22} />
                    </div>
                    <div>
                      <p className="font-bold text-white text-lg">{formatCurrency(settlement.amount)}</p>
                      <p className="text-sm text-zinc-500">
                        {settlement.event_title || 'General'} • {formatDate(settlement.requested_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {getStatusBadge(settlement.status)}
                    {settlement.payment_proof_url && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(settlement.payment_proof_url, '_blank');
                        }}
                        className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                      >
                        <FileText size={14} className="mr-1" />
                        Comprobante
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Request Modal */}
      <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <DollarSign className="text-emerald-400" size={20} />
              Solicitar Liquidación
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Ingresá el monto que querés solicitar
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Available Balance */}
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 text-center">
              <p className="text-xs text-emerald-400 uppercase tracking-wide">Saldo Disponible</p>
              <p className="text-3xl font-black text-emerald-400 mt-1">
                {formatCurrency(earnings?.available_balance)}
              </p>
            </div>
            
            {/* Amount Input */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                Monto a solicitar
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                  <Input
                    type="number"
                    value={requestAmount}
                    onChange={(e) => setRequestAmount(e.target.value)}
                    placeholder="0"
                    max={earnings?.available_balance}
                    className="pl-8 bg-zinc-800 border-zinc-700 text-lg font-bold"
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={() => setRequestAmount(earnings?.available_balance?.toString() || '0')}
                  className="border-zinc-700 whitespace-nowrap"
                >
                  Todo
                </Button>
              </div>
            </div>
            
            {/* Info */}
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 flex items-start gap-2">
              <Info className="text-blue-400 flex-shrink-0 mt-0.5" size={16} />
              <div className="text-xs text-blue-300">
                <p className="font-medium mb-1">Proceso de pago:</p>
                <ul className="list-disc list-inside text-blue-200/70 space-y-0.5">
                  <li>Tu solicitud será revisada por el equipo</li>
                  <li>Una vez aprobada, procesaremos el pago</li>
                  <li>Recibirás un email con el comprobante</li>
                  <li>El pago puede demorar 24-48hs hábiles según tu banco</li>
                </ul>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowRequestModal(false)}
              className="border-zinc-700"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleRequestSettlement}
              disabled={!requestAmount || parseFloat(requestAmount) <= 0 || requesting}
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
            >
              {requesting ? (
                <Loader2 className="animate-spin mr-2" size={16} />
              ) : (
                <Send size={16} className="mr-2" />
              )}
              Enviar Solicitud
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={detailModal.open} onOpenChange={(open) => !open && setDetailModal({ open: false, settlement: null })}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <FileText className="text-blue-400" size={20} />
              Detalle de Liquidación
            </DialogTitle>
          </DialogHeader>
          
          {detailModal.settlement && (
            <div className="space-y-4 py-4">
              {/* Amount & Status */}
              <div className="text-center">
                <p className="text-4xl font-black text-emerald-400">
                  {formatCurrency(detailModal.settlement.amount)}
                </p>
                <div className="mt-3">{getStatusBadge(detailModal.settlement.status)}</div>
              </div>
              
              {/* Details */}
              <div className="space-y-3">
                <div className="bg-zinc-800/50 rounded-lg p-3 flex justify-between">
                  <span className="text-zinc-400 text-sm">ID</span>
                  <span className="text-white font-mono text-sm">{detailModal.settlement.id}</span>
                </div>
                
                {detailModal.settlement.event_title && (
                  <div className="bg-zinc-800/50 rounded-lg p-3 flex justify-between">
                    <span className="text-zinc-400 text-sm">Evento/Contrato</span>
                    <span className="text-white text-sm">{detailModal.settlement.event_title}</span>
                  </div>
                )}
                
                <div className="bg-zinc-800/50 rounded-lg p-3 flex justify-between">
                  <span className="text-zinc-400 text-sm">Solicitado</span>
                  <span className="text-white text-sm">{formatDateTime(detailModal.settlement.requested_at)}</span>
                </div>
                
                {detailModal.settlement.approved_at && (
                  <div className="bg-zinc-800/50 rounded-lg p-3 flex justify-between">
                    <span className="text-zinc-400 text-sm">Aprobado</span>
                    <span className="text-white text-sm">{formatDateTime(detailModal.settlement.approved_at)}</span>
                  </div>
                )}
                
                {detailModal.settlement.paid_at && (
                  <div className="bg-zinc-800/50 rounded-lg p-3 flex justify-between">
                    <span className="text-zinc-400 text-sm">Pagado</span>
                    <span className="text-white text-sm">{formatDateTime(detailModal.settlement.paid_at)}</span>
                  </div>
                )}
              </div>
              
              {/* Rejection Reason */}
              {detailModal.settlement.admin_notes && detailModal.settlement.status === 'rejected' && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                  <p className="text-xs text-red-400 font-semibold mb-1">Motivo del rechazo:</p>
                  <p className="text-sm text-red-300">{detailModal.settlement.admin_notes}</p>
                </div>
              )}
              
              {/* Payment Proof */}
              {detailModal.settlement.payment_proof_url && (
                <Button
                  className="w-full bg-emerald-600 hover:bg-emerald-500"
                  onClick={() => window.open(detailModal.settlement.payment_proof_url, '_blank')}
                >
                  <ExternalLink size={16} className="mr-2" />
                  Ver Comprobante de Pago
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};


// ============ Settings Tab Component ============
const SettingsTab = ({ press, onRefresh }) => {
  const [activeSection, setActiveSection] = useState('profile');
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  // Service Categories (NEW)
  const SERVICE_CATEGORIES = [
    { value: 'influencer', label: 'Influencer / Creador de Contenido', icon: 'sparkles' },
    { value: 'photographer', label: 'Fotógrafo/a', icon: 'camera' },
    { value: 'videographer', label: 'Camarógrafo/a / Videógrafo', icon: 'video' },
    { value: 'digital_media', label: 'Medio Digital / Blog', icon: 'globe' },
    { value: 'journalist', label: 'Periodista / Comunicador', icon: 'newspaper' },
    { value: 'radio', label: 'Radio / Podcast', icon: 'radio' },
    { value: 'dj_producer', label: 'DJ / Productor Musical', icon: 'music' },
    { value: 'social_media_manager', label: 'Community Manager', icon: 'share' },
    { value: 'graphic_designer', label: 'Diseñador/a Gráfico', icon: 'palette' },
    { value: 'event_coverage', label: 'Cobertura de Eventos', icon: 'calendar' },
    { value: 'marketing', label: 'Marketing / Publicidad', icon: 'megaphone' },
    { value: 'other', label: 'Otro', icon: 'more-horizontal' }
  ];

  // Profile form
  const [profileForm, setProfileForm] = useState({
    media_name: press?.media_name || '',
    contact_name: press?.contact_name || '',
    email: press?.email || '',
    phone: press?.phone || '',
    city: press?.city || '',
    province: press?.province || '',
    description: press?.description || ''
  });

  // Service categories state (NEW)
  const [serviceCategories, setServiceCategories] = useState(press?.service_categories || []);

  // Images
  const [logoUrl, setLogoUrl] = useState(press?.logo_url || '');
  const [bannerUrl, setBannerUrl] = useState(press?.banner_url || press?.cover_image_url || '');

  // Bank info
  const [bankForm, setBankForm] = useState({
    bank_name: press?.bank_info?.bank_name || '',
    account_holder: press?.bank_info?.account_holder || '',
    cbu: press?.bank_info?.cbu || '',
    alias: press?.bank_info?.alias || '',
    cuit: press?.bank_info?.cuit || ''
  });

  // Social networks
  const [socialNetworks, setSocialNetworks] = useState(press?.social_networks || []);
  const [newSocial, setNewSocial] = useState({ platform: 'instagram', url: '', followers: '' });

  // Sync all form states when press data loads/changes
  useEffect(() => {
    if (press) {
      // Sync profile form
      setProfileForm({
        media_name: press.media_name || '',
        contact_name: press.contact_name || '',
        email: press.email || '',
        phone: press.phone || '',
        city: press.city || '',
        province: press.province || '',
        description: press.description || ''
      });
      
      // Sync service categories
      setServiceCategories(press.service_categories || []);
      
      // Sync images
      setLogoUrl(press.logo_url || '');
      setBannerUrl(press.banner_url || press.cover_image_url || '');
      
      // Sync bank form
      setBankForm({
        bank_name: press.bank_info?.bank_name || '',
        account_holder: press.bank_info?.account_holder || '',
        cbu: press.bank_info?.cbu || '',
        alias: press.bank_info?.alias || '',
        cuit: press.bank_info?.cuit || ''
      });
      
      // Sync social networks
      setSocialNetworks(press.social_networks || []);
      
      // Sync fee setting
      setAbsorbsFee(press.absorbs_platform_fee || false);
    }
  }, [press]);

  // Audience - Load ALL fields from press.audience
  const [audienceForm, setAudienceForm] = useState({
    age_range_min: press?.audience?.age_range_min || 18,
    age_range_max: press?.audience?.age_range_max || 35,
    gender_distribution: press?.audience?.gender_distribution || 'mixed',
    primary_interests: press?.audience?.primary_interests || [],
    // Additional fields that were missing
    education_level: press?.audience?.education_level || '',
    income_level: press?.audience?.income_level || '',
    peak_activity_hours: press?.audience?.peak_activity_hours || '',
    content_preferences: press?.audience?.content_preferences || [],
    interests: press?.audience?.interests || [],
    lifestyle: press?.audience?.lifestyle || [],
    locations: press?.audience?.locations || [],
    occupation_types: press?.audience?.occupation_types || [],
    custom_demographics: press?.audience?.custom_demographics || []
  });

  // Fee settings
  const [absorbsFee, setAbsorbsFee] = useState(press?.absorbs_platform_fee || false);

  // Sync audienceForm when press data loads/changes (fixes async loading issue)
  useEffect(() => {
    if (press?.audience) {
      setAudienceForm({
        age_range_min: press.audience.age_range_min || 18,
        age_range_max: press.audience.age_range_max || 35,
        gender_distribution: press.audience.gender_distribution || 'mixed',
        primary_interests: press.audience.primary_interests || [],
        education_level: press.audience.education_level || '',
        income_level: press.audience.income_level || '',
        peak_activity_hours: press.audience.peak_activity_hours || '',
        content_preferences: press.audience.content_preferences || [],
        interests: press.audience.interests || [],
        lifestyle: press.audience.lifestyle || [],
        locations: press.audience.locations || [],
        occupation_types: press.audience.occupation_types || [],
        custom_demographics: press.audience.custom_demographics || []
      });
    }
  }, [press]);

  // Password
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await api.put('/press/me', {
        ...profileForm,
        service_categories: serviceCategories
      });
      toast.success('Perfil actualizado');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Toggle service category (NEW)
  const toggleServiceCategory = (category) => {
    setServiceCategories(prev => 
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const handleImageUpload = async (file, type) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'press_images');

    const setUploading = type === 'logo' ? setUploadingLogo : setUploadingBanner;
    setUploading(true);

    try {
      const res = await api.post('/cloudinary/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const imageUrl = res.data.secure_url || res.data.url;
      
      // Update press profile with new image
      const updateData = type === 'logo' 
        ? { logo_url: imageUrl }
        : { banner_url: imageUrl, cover_image_url: imageUrl };
      
      await api.put('/press/me', updateData);
      
      if (type === 'logo') {
        setLogoUrl(imageUrl);
      } else {
        setBannerUrl(imageUrl);
      }
      
      toast.success(`${type === 'logo' ? 'Logo' : 'Banner'} actualizado`);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir imagen');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveBankInfo = async () => {
    setSaving(true);
    try {
      await api.put('/press/me', { bank_info: bankForm });
      toast.success('Datos bancarios guardados');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Validate URL format for social networks
  const isValidSocialUrl = (url, platform) => {
    // Basic URL pattern - must start with http:// or https://
    const urlPattern = /^https?:\/\/.+/i;
    if (!urlPattern.test(url)) {
      return false;
    }
    
    // Platform-specific validation
    const platformPatterns = {
      instagram: /^https?:\/\/(www\.)?instagram\.com\/.+/i,
      tiktok: /^https?:\/\/(www\.)?(tiktok\.com|vm\.tiktok\.com)\/.+/i,
      twitter: /^https?:\/\/(www\.)?(twitter\.com|x\.com)\/.+/i,
      facebook: /^https?:\/\/(www\.)?facebook\.com\/.+/i,
      youtube: /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\/.+/i,
      website: /^https?:\/\/.+/i  // Any valid URL for website
    };
    
    const pattern = platformPatterns[platform] || platformPatterns.website;
    return pattern.test(url);
  };

  const handleAddSocialNetwork = () => {
    if (!newSocial.url.trim()) {
      toast.error('Ingresá la URL');
      return;
    }
    
    // Validate that it's a proper URL, not just a username
    if (!isValidSocialUrl(newSocial.url, newSocial.platform)) {
      toast.error('Ingresá un enlace válido (ej: https://instagram.com/tu_usuario). No se permiten nombres de usuario solos.');
      return;
    }
    
    const updated = [...socialNetworks, {
      platform: newSocial.platform,
      url: newSocial.url,
      followers: parseInt(newSocial.followers) || 0
    }];
    
    setSocialNetworks(updated);
    setNewSocial({ platform: 'instagram', url: '', followers: '' });
  };

  const handleRemoveSocialNetwork = (index) => {
    setSocialNetworks(socialNetworks.filter((_, i) => i !== index));
  };

  const handleSaveSocialNetworks = async () => {
    setSaving(true);
    try {
      await api.put('/press/me', { social_networks: socialNetworks });
      toast.success('Redes sociales actualizadas');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current_password || !passwordForm.new_password) {
      toast.error('Completá todos los campos');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setSaving(true);
    try {
      await api.put('/press/me/password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success('Contraseña actualizada');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar contraseña');
    } finally {
      setSaving(false);
    }
  };

  const socialPlatforms = [
    { value: 'instagram', label: 'Instagram', icon: Instagram },
    { value: 'tiktok', label: 'TikTok', icon: Music },
    { value: 'twitter', label: 'Twitter/X', icon: Twitter },
    { value: 'facebook', label: 'Facebook', icon: Facebook },
    { value: 'youtube', label: 'YouTube', icon: Youtube },
    { value: 'website', label: 'Sitio Web', icon: Globe }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div>
        <h1 className="text-2xl font-black text-white mb-2">Configuración</h1>
        <p className="text-zinc-400">Editá tu perfil y preferencias</p>
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <nav className="space-y-1">
            {[
              { id: 'profile', label: 'Datos del Medio', icon: Newspaper },
              { id: 'audience', label: 'Target/Audiencia', icon: Users },
              { id: 'images', label: 'Logo y Banner', icon: Camera },
              { id: 'bank', label: 'Datos Bancarios', icon: CreditCard },
              { id: 'social', label: 'Redes Sociales', icon: LinkIcon },
              { id: 'fees', label: 'Costos Servicio', icon: DollarSign },
              { id: 'security', label: 'Seguridad', icon: Lock }
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveSection(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  activeSection === item.id
                    ? 'bg-blue-500/20 text-blue-400'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
                data-testid={`settings-${item.id}`}
              >
                <item.icon size={18} />
                <span className="font-medium text-sm">{item.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          {/* Profile Section */}
          {activeSection === 'profile' && (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
              <h2 className="text-lg font-bold text-white mb-4">Datos del Medio</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400">Nombre del Medio</Label>
                  <Input
                    value={profileForm.media_name}
                    onChange={(e) => setProfileForm({ ...profileForm, media_name: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Nombre de Contacto</Label>
                  <Input
                    value={profileForm.contact_name}
                    onChange={(e) => setProfileForm({ ...profileForm, contact_name: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Email</Label>
                  <Input
                    type="email"
                    value={profileForm.email}
                    onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Teléfono</Label>
                  <Input
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Ciudad</Label>
                  <Input
                    value={profileForm.city}
                    onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Provincia</Label>
                  <Input
                    value={profileForm.province}
                    onChange={(e) => setProfileForm({ ...profileForm, province: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-zinc-400">Descripción</Label>
                  <Textarea
                    value={profileForm.description}
                    onChange={(e) => setProfileForm({ ...profileForm, description: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 mt-1"
                    rows={3}
                  />
                </div>

                {/* Service Categories - NEW */}
                <div className="sm:col-span-2">
                  <Label className="text-zinc-400 mb-3 block">
                    Categorías de Servicio
                    <span className="text-zinc-600 font-normal ml-2">(Seleccioná todas las que apliquen)</span>
                  </Label>
                  <p className="text-zinc-500 text-xs mb-3">
                    Esto ayuda a las productoras a encontrarte según el tipo de servicio que necesitan
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {SERVICE_CATEGORIES.map((cat) => (
                      <button
                        key={cat.value}
                        type="button"
                        onClick={() => toggleServiceCategory(cat.value)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-left text-sm transition-all ${
                          serviceCategories.includes(cat.value)
                            ? 'bg-blue-500/20 border-blue-500/50 text-blue-400'
                            : 'bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                        }`}
                      >
                        {serviceCategories.includes(cat.value) ? (
                          <Check size={14} className="flex-shrink-0" />
                        ) : (
                          <div className="w-3.5 h-3.5 rounded border border-zinc-600 flex-shrink-0" />
                        )}
                        <span className="truncate">{cat.label}</span>
                      </button>
                    ))}
                  </div>
                  {serviceCategories.length > 0 && (
                    <p className="text-blue-400 text-xs mt-2">
                      {serviceCategories.length} categoría{serviceCategories.length > 1 ? 's' : ''} seleccionada{serviceCategories.length > 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              </div>
              <Button 
                className="mt-4 bg-blue-500 hover:bg-blue-600"
                onClick={handleSaveProfile}
                disabled={saving}
              >
                {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
                Guardar Cambios
              </Button>
            </div>
          )}

          {/* Audience Section - Enhanced */}
          {activeSection === 'audience' && (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
              <h2 className="text-lg font-bold text-white mb-2">Target de Audiencia</h2>
              <p className="text-zinc-500 text-sm mb-6">Define el perfil completo de tu audiencia para que las productoras puedan evaluar mejor tu alcance</p>
              
              <div className="space-y-8">
                {/* Demographics Section */}
                <div>
                  <h3 className="text-blue-400 font-medium mb-4 flex items-center gap-2">
                    <Users size={18} />
                    Datos Demográficos
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Age Range */}
                    <div>
                      <Label className="text-zinc-400 mb-2 block">Rango de Edad</Label>
                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <Label className="text-zinc-500 text-xs">Desde</Label>
                          <Select 
                            value={audienceForm.age_range_min?.toString() || '18'} 
                            onValueChange={(v) => setAudienceForm({ ...audienceForm, age_range_min: parseInt(v) })}
                          >
                            <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-800 border-zinc-700">
                              {[13, 16, 18, 21, 25, 30, 35, 40, 45, 50, 55].map(age => (
                                <SelectItem key={age} value={age.toString()}>{age} años</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <span className="text-zinc-500 mt-6">-</span>
                        <div className="flex-1">
                          <Label className="text-zinc-500 text-xs">Hasta</Label>
                          <Select 
                            value={audienceForm.age_range_max?.toString() || '35'} 
                            onValueChange={(v) => setAudienceForm({ ...audienceForm, age_range_max: parseInt(v) })}
                          >
                            <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-800 border-zinc-700">
                              {[18, 24, 30, 35, 40, 45, 50, 55, 60, 65].map(age => (
                                <SelectItem key={age} value={age.toString()}>{age} años</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    {/* Gender Distribution */}
                    <div>
                      <Label className="text-zinc-400 mb-2 block">Género Predominante</Label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {[
                          { value: 'male', label: 'Masculino' },
                          { value: 'female', label: 'Femenino' },
                          { value: 'mixed', label: 'Mixto' }
                        ].map(option => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => setAudienceForm({ ...audienceForm, gender_distribution: option.value })}
                            className={`px-4 py-2 rounded-lg border transition-all ${
                              audienceForm.gender_distribution === option.value
                                ? 'bg-blue-500/20 border-blue-500 text-blue-400'
                                : 'bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Education Level */}
                    <div>
                      <Label className="text-zinc-400 mb-2 block">Nivel Educativo</Label>
                      <Select 
                        value={audienceForm.education_level || ''} 
                        onValueChange={(v) => setAudienceForm({ ...audienceForm, education_level: v })}
                      >
                        <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          <SelectItem value="secundario">Secundario</SelectItem>
                          <SelectItem value="universitario">Universitario</SelectItem>
                          <SelectItem value="profesional">Profesional</SelectItem>
                          <SelectItem value="mixto">Mixto</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Income Level */}
                    <div>
                      <Label className="text-zinc-400 mb-2 block">Nivel Económico</Label>
                      <Select 
                        value={audienceForm.income_level || ''} 
                        onValueChange={(v) => setAudienceForm({ ...audienceForm, income_level: v })}
                      >
                        <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          <SelectItem value="sin_definir">Sin Definir</SelectItem>
                          <SelectItem value="mixto">Mixto (Todos los niveles)</SelectItem>
                          <SelectItem value="bajo">Bajo</SelectItem>
                          <SelectItem value="medio-bajo">Medio-Bajo</SelectItem>
                          <SelectItem value="medio">Medio</SelectItem>
                          <SelectItem value="medio-alto">Medio-Alto</SelectItem>
                          <SelectItem value="alto">Alto</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Activity & Behavior Section */}
                <div>
                  <h3 className="text-green-400 font-medium mb-4 flex items-center gap-2">
                    <Clock size={18} />
                    Actividad y Comportamiento
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Peak Activity Hours */}
                    <div>
                      <Label className="text-zinc-400 mb-2 block">Horario de Mayor Actividad</Label>
                      <Select 
                        value={audienceForm.peak_activity_hours || ''} 
                        onValueChange={(v) => setAudienceForm({ ...audienceForm, peak_activity_hours: v })}
                      >
                        <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          <SelectItem value="06-12hs">Mañana (06-12hs)</SelectItem>
                          <SelectItem value="12-18hs">Tarde (12-18hs)</SelectItem>
                          <SelectItem value="18-22hs">Noche (18-22hs)</SelectItem>
                          <SelectItem value="22-02hs">Noche (22-02hs)</SelectItem>
                          <SelectItem value="todo-el-dia">Todo el día</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Content Preferences */}
                    <div>
                      <Label className="text-zinc-400 mb-2 block">Preferencias de Contenido</Label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {['Fotos', 'Videos', 'Stories', 'Reels', 'Lives', 'Carruseles'].map(pref => {
                          const normalize = (s) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                          const isSelected = (audienceForm.content_preferences || []).some(i => normalize(i) === normalize(pref));
                          return (
                          <button
                            key={pref}
                            type="button"
                            onClick={() => {
                              const current = audienceForm.content_preferences || [];
                              const matchingValue = current.find(i => normalize(i) === normalize(pref));
                              setAudienceForm({
                                ...audienceForm,
                                content_preferences: matchingValue
                                  ? current.filter(i => i !== matchingValue)
                                  : [...current, pref]
                              });
                            }}
                            className={`px-3 py-1.5 rounded-lg border text-sm transition-all ${
                              isSelected
                                ? 'bg-cyan-500/20 border-cyan-500 text-cyan-400'
                                : 'bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                            }`}
                          >
                            {pref}
                          </button>
                        );})}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Interests Section */}
                <div>
                  <h3 className="text-purple-400 font-medium mb-4 flex items-center gap-2">
                    <Sparkles size={18} />
                    Intereses
                  </h3>
                  
                  {/* Primary Interests */}
                  <div className="mb-4">
                    <Label className="text-zinc-400 mb-2 block">Intereses Musicales / Entretenimiento</Label>
                    <div className="flex flex-wrap gap-2">
                      {['Música Electrónica', 'Rock', 'Pop', 'Reggaeton', 'Hip Hop', 'Cumbia', 'Trap', 'Jazz', 'Festivales', 'Vida Nocturna', 'Conciertos'].map(interest => {
                        // Flexible match: compare normalized versions (lowercase, no accents)
                        const normalize = (s) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                        const isSelected = (audienceForm.interests || []).some(i => 
                          normalize(i) === normalize(interest) || 
                          normalize(interest).includes(normalize(i)) ||
                          normalize(i).includes(normalize(interest))
                        );
                        return (
                        <button
                          key={interest}
                          type="button"
                          onClick={() => {
                            const interests = audienceForm.interests || [];
                            const matchingValue = interests.find(i => 
                              normalize(i) === normalize(interest) || 
                              normalize(interest).includes(normalize(i)) ||
                              normalize(i).includes(normalize(interest))
                            );
                            setAudienceForm({
                              ...audienceForm,
                              interests: matchingValue
                                ? interests.filter(i => i !== matchingValue)
                                : [...interests, interest]
                            });
                          }}
                          className={`px-3 py-1.5 rounded-lg border text-sm transition-all ${
                            isSelected
                              ? 'bg-purple-500/20 border-purple-500 text-purple-400'
                              : 'bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                          }`}
                        >
                          {interest}
                        </button>
                      );})}
                    </div>
                  </div>

                  {/* Lifestyle */}
                  <div>
                    <Label className="text-zinc-400 mb-2 block">Estilo de Vida</Label>
                    <div className="flex flex-wrap gap-2">
                      {['Nocturno', 'Deportivo', 'Cultural', 'Tecnológico', 'Saludable', 'Viajero', 'Foodie', 'Fashion', 'Artístico', 'Emprendedor'].map(ls => {
                        // Flexible match for lifestyle
                        const normalize = (s) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                        const isSelected = (audienceForm.lifestyle || []).some(i => 
                          normalize(i) === normalize(ls)
                        );
                        return (
                        <button
                          key={ls}
                          type="button"
                          onClick={() => {
                            const current = audienceForm.lifestyle || [];
                            const matchingValue = current.find(i => normalize(i) === normalize(ls));
                            setAudienceForm({
                              ...audienceForm,
                              lifestyle: matchingValue
                                ? current.filter(i => i !== matchingValue)
                                : [...current, ls]
                            });
                          }}
                          className={`px-3 py-1.5 rounded-lg border text-sm transition-all ${
                            isSelected
                              ? 'bg-orange-500/20 border-orange-500 text-orange-400'
                              : 'bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                          }`}
                        >
                          {ls}
                        </button>
                      );})}
                    </div>
                  </div>
                </div>

                {/* Location & Occupation Section */}
                <div>
                  <h3 className="text-yellow-400 font-medium mb-4 flex items-center gap-2">
                    <MapPin size={18} />
                    Ubicación y Ocupación
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Locations */}
                    <div>
                      <Label className="text-zinc-400 mb-2 block">Ubicaciones Principales</Label>
                      <div className="flex flex-wrap gap-2">
                        {['CABA', 'Buenos Aires', 'Córdoba', 'Rosario', 'Mendoza', 'Santa Fe', 'Tucumán', 'Mar del Plata', 'Bariloche', 'Interior'].map(loc => (
                          <button
                            key={loc}
                            type="button"
                            onClick={() => {
                              const current = audienceForm.locations || [];
                              setAudienceForm({
                                ...audienceForm,
                                locations: current.includes(loc)
                                  ? current.filter(i => i !== loc)
                                  : [...current, loc]
                              });
                            }}
                            className={`px-3 py-1.5 rounded-lg border text-sm transition-all ${
                              (audienceForm.locations || []).includes(loc)
                                ? 'bg-yellow-500/20 border-yellow-500 text-yellow-400'
                                : 'bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                            }`}
                          >
                            {loc}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Occupation Types */}
                    <div>
                      <Label className="text-zinc-400 mb-2 block">Ocupaciones Típicas</Label>
                      <div className="flex flex-wrap gap-2">
                        {['Estudiantes', 'Profesionales', 'Emprendedores', 'Freelancers', 'Artistas', 'Corporativos'].map(occ => {
                          const normalize = (s) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                          const isSelected = (audienceForm.occupation_types || []).some(i => normalize(i) === normalize(occ));
                          return (
                          <button
                            key={occ}
                            type="button"
                            onClick={() => {
                              const current = audienceForm.occupation_types || [];
                              const matchingValue = current.find(i => normalize(i) === normalize(occ));
                              setAudienceForm({
                                ...audienceForm,
                                occupation_types: matchingValue
                                  ? current.filter(i => i !== matchingValue)
                                  : [...current, occ]
                              });
                            }}
                            className={`px-3 py-1.5 rounded-lg border text-sm transition-all ${
                              isSelected
                                ? 'bg-green-500/20 border-green-500 text-green-400'
                                : 'bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                            }`}
                          >
                            {occ}
                          </button>
                        );})}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Custom Demographics Section */}
                <div>
                  <h3 className="text-pink-400 font-medium mb-4 flex items-center gap-2">
                    <Plus size={18} />
                    Datos Personalizados
                  </h3>
                  <p className="text-zinc-500 text-sm mb-3">Añade características adicionales que definen a tu audiencia</p>
                  
                  {/* Existing custom fields */}
                  {(audienceForm.custom_demographics || []).length > 0 && (
                    <div className="space-y-2 mb-4">
                      {audienceForm.custom_demographics.map((cd, i) => (
                        <div key={i} className="flex items-center gap-2 bg-zinc-800/50 rounded-lg px-3 py-2">
                          <span className="text-zinc-400 flex-1">{cd.name}:</span>
                          <span className="text-white font-medium">{cd.value}</span>
                          <button
                            type="button"
                            onClick={() => {
                              const updated = audienceForm.custom_demographics.filter((_, idx) => idx !== i);
                              setAudienceForm({ ...audienceForm, custom_demographics: updated });
                            }}
                            className="text-red-400 hover:text-red-300"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Add new custom field */}
                  <div className="flex gap-2">
                    <Input
                      placeholder="Característica (ej: NSE)"
                      className="bg-zinc-800 border-zinc-700 flex-1"
                      id="custom-demo-name"
                    />
                    <Input
                      placeholder="Valor (ej: ABC1)"
                      className="bg-zinc-800 border-zinc-700 flex-1"
                      id="custom-demo-value"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        const name = document.getElementById('custom-demo-name').value;
                        const value = document.getElementById('custom-demo-value').value;
                        if (name && value) {
                          setAudienceForm({
                            ...audienceForm,
                            custom_demographics: [...(audienceForm.custom_demographics || []), { name, value }]
                          });
                          document.getElementById('custom-demo-name').value = '';
                          document.getElementById('custom-demo-value').value = '';
                        }
                      }}
                    >
                      <Plus size={16} />
                    </Button>
                  </div>
                </div>
              </div>
              
              <Button 
                className="mt-6 bg-blue-500 hover:bg-blue-600"
                onClick={async () => {
                  setSaving(true);
                  try {
                    await api.put('/press/me', { audience: audienceForm });
                    toast.success('Audiencia actualizada');
                    onRefresh();
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Error al guardar');
                  } finally {
                    setSaving(false);
                  }
                }}
                disabled={saving}
              >
                {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
                Guardar Audiencia
              </Button>
            </div>
          )}

          {/* Images Section */}
          {activeSection === 'images' && (
            <div className="space-y-6">
              {/* Logo */}
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
                <h2 className="text-lg font-bold text-white mb-2">Logo</h2>
                <p className="text-zinc-500 text-sm mb-4">Tamaño recomendado: 200x200px (cuadrado)</p>
                
                <div className="flex items-start gap-4">
                  <div className="w-24 h-24 bg-zinc-800 rounded-xl overflow-hidden flex items-center justify-center border border-zinc-700">
                    {logoUrl ? (
                      <img src={logoUrl} alt="Logo" className="w-full h-full object-cover" />
                    ) : (
                      <Newspaper className="text-zinc-600" size={32} />
                    )}
                  </div>
                  <div>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleImageUpload(e.target.files[0], 'logo')}
                    />
                    <Button
                      variant="outline"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingLogo}
                    >
                      {uploadingLogo ? (
                        <><Loader2 className="animate-spin mr-2" size={16} /> Subiendo...</>
                      ) : (
                        <><Upload size={16} className="mr-2" /> Subir Logo</>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Banner */}
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
                <h2 className="text-lg font-bold text-white mb-2">Banner</h2>
                <p className="text-zinc-500 text-sm mb-4">Tamaño recomendado: 1200x400px (horizontal)</p>
                
                <div className="space-y-4">
                  <div className="w-full h-40 bg-zinc-800 rounded-xl overflow-hidden flex items-center justify-center border border-zinc-700">
                    {bannerUrl ? (
                      <img src={bannerUrl} alt="Banner" className="w-full h-full object-cover" />
                    ) : (
                      <Image className="text-zinc-600" size={48} />
                    )}
                  </div>
                  <input
                    ref={bannerInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleImageUpload(e.target.files[0], 'banner')}
                  />
                  <Button
                    variant="outline"
                    onClick={() => bannerInputRef.current?.click()}
                    disabled={uploadingBanner}
                  >
                    {uploadingBanner ? (
                      <><Loader2 className="animate-spin mr-2" size={16} /> Subiendo...</>
                    ) : (
                      <><Upload size={16} className="mr-2" /> Subir Banner</>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Bank Section */}
          {activeSection === 'bank' && (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
              <h2 className="text-lg font-bold text-white mb-2">Datos Bancarios</h2>
              <p className="text-zinc-500 text-sm mb-4">Para recibir los pagos de tus servicios</p>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400">Banco</Label>
                  <Input
                    value={bankForm.bank_name}
                    onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })}
                    placeholder="Ej: Banco Galicia"
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Titular de la cuenta</Label>
                  <Input
                    value={bankForm.account_holder}
                    onChange={(e) => setBankForm({ ...bankForm, account_holder: e.target.value })}
                    placeholder="Nombre completo"
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">CBU</Label>
                  <Input
                    value={bankForm.cbu}
                    onChange={(e) => setBankForm({ ...bankForm, cbu: e.target.value })}
                    placeholder="22 dígitos"
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Alias</Label>
                  <Input
                    value={bankForm.alias}
                    onChange={(e) => setBankForm({ ...bankForm, alias: e.target.value })}
                    placeholder="tu.alias"
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">CUIT/CUIL</Label>
                  <Input
                    value={bankForm.cuit}
                    onChange={(e) => setBankForm({ ...bankForm, cuit: e.target.value })}
                    placeholder="XX-XXXXXXXX-X"
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>
              </div>
              
              <Button 
                className="mt-4 bg-blue-500 hover:bg-blue-600"
                onClick={handleSaveBankInfo}
                disabled={saving}
              >
                {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
                Guardar Datos Bancarios
              </Button>
            </div>
          )}

          {/* Social Networks Section */}
          {activeSection === 'social' && (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
              <h2 className="text-lg font-bold text-white mb-4">Redes Sociales</h2>
              
              {/* Add new */}
              <div className="flex flex-wrap gap-2 mb-4 pb-4 border-b border-zinc-800">
                <Select
                  value={newSocial.platform}
                  onValueChange={(value) => setNewSocial({ ...newSocial, platform: value })}
                >
                  <SelectTrigger className="w-36 bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {socialPlatforms.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={newSocial.url}
                  onChange={(e) => setNewSocial({ ...newSocial, url: e.target.value })}
                  placeholder="https://instagram.com/tu_perfil"
                  className="bg-zinc-800 border-zinc-700 flex-1 min-w-[200px]"
                />
                <Input
                  type="number"
                  value={newSocial.followers}
                  onChange={(e) => setNewSocial({ ...newSocial, followers: e.target.value })}
                  placeholder="Seguidores"
                  className="bg-zinc-800 border-zinc-700 w-28"
                />
                <Button onClick={handleAddSocialNetwork} variant="outline">
                  <Plus size={16} />
                </Button>
              </div>

              {/* List */}
              <div className="space-y-2">
                {socialNetworks.map((network, index) => {
                  const platform = socialPlatforms.find(p => p.value === network.platform);
                  const Icon = platform?.icon || Globe;
                  return (
                    <div
                      key={index}
                      className="flex items-center gap-3 bg-zinc-800/50 rounded-lg p-3"
                    >
                      <Icon size={20} className="text-zinc-400" />
                      <span className="text-white capitalize flex-shrink-0">{network.platform}</span>
                      <a 
                        href={network.url.startsWith('http') ? network.url : `https://${network.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 text-sm truncate flex-1 hover:underline flex items-center gap-1"
                      >
                        {network.url}
                        <ExternalLink size={12} />
                      </a>
                      {network.followers > 0 && (
                        <span className="text-green-400 text-sm">{network.followers.toLocaleString()} seguidores</span>
                      )}
                      <button
                        onClick={() => handleRemoveSocialNetwork(index)}
                        className="text-zinc-400 hover:text-red-400"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  );
                })}
              </div>

              {socialNetworks.length === 0 && (
                <p className="text-zinc-500 text-center py-4">No tenés redes sociales configuradas</p>
              )}

              <Button 
                className="mt-4 bg-blue-500 hover:bg-blue-600"
                onClick={handleSaveSocialNetworks}
                disabled={saving}
              >
                {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
                Guardar Redes Sociales
              </Button>
            </div>
          )}

          {/* Fees Section */}
          {activeSection === 'fees' && (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
              <h2 className="text-lg font-bold text-white mb-2">Costos de Servicio de Plataforma</h2>
              <p className="text-zinc-500 text-sm mb-6">
                La plataforma cobra un costo de servicio por cada trabajo contratado. Podés elegir quién asume este costo.
              </p>
              
              <div className="space-y-6">
                <div className="bg-zinc-800/50 rounded-xl p-4">
                  <div className="flex items-start gap-4">
                    <input
                      type="checkbox"
                      id="absorbs_fee"
                      checked={absorbsFee}
                      onChange={(e) => setAbsorbsFee(e.target.checked)}
                      className="rounded bg-zinc-700 border-zinc-600 mt-1"
                    />
                    <div>
                      <Label htmlFor="absorbs_fee" className="text-white font-medium cursor-pointer">
                        Absorber el costo de servicio de plataforma
                      </Label>
                      <p className="text-zinc-500 text-sm mt-1">
                        Si activás esta opción, la productora pagará solo el monto cotizado y el costo de servicio se descontará de tu pago.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                  <h4 className="text-blue-400 font-medium mb-2">¿Cómo funciona?</h4>
                  <div className="text-sm text-zinc-400 space-y-2">
                    <p><strong>Sin absorber costo de servicio (por defecto):</strong></p>
                    <p className="pl-4">• Cotizás $10.000</p>
                    <p className="pl-4">• La productora paga $10.000 + costo de servicio = $11.000</p>
                    <p className="pl-4">• Vos recibís $10.000</p>
                    
                    <p className="mt-3"><strong>Absorbiendo costo de servicio:</strong></p>
                    <p className="pl-4">• Cotizás $10.000</p>
                    <p className="pl-4">• La productora paga $10.000</p>
                    <p className="pl-4">• Vos recibís $10.000 - costo de servicio = $9.000</p>
                  </div>
                </div>
              </div>
              
              <Button 
                className="mt-6 bg-blue-500 hover:bg-blue-600"
                onClick={async () => {
                  setSaving(true);
                  try {
                    await api.put('/press/me', { absorbs_platform_fee: absorbsFee });
                    toast.success('Configuración de costos actualizada');
                    onRefresh();
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Error al guardar');
                  } finally {
                    setSaving(false);
                  }
                }}
                disabled={saving}
              >
                {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
                Guardar Configuración
              </Button>
            </div>
          )}

          {/* Security Section */}
          {activeSection === 'security' && (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
              <h2 className="text-lg font-bold text-white mb-4">Cambiar Contraseña</h2>
              
              <div className="space-y-4 max-w-md">
                <div>
                  <Label className="text-zinc-400">Contraseña actual</Label>
                  <Input
                    type="password"
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Nueva contraseña</Label>
                  <Input
                    type="password"
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Confirmar nueva contraseña</Label>
                  <Input
                    type="password"
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>
                
                <Button 
                  className="bg-blue-500 hover:bg-blue-600"
                  onClick={handleChangePassword}
                  disabled={saving}
                >
                  {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Lock size={16} className="mr-2" />}
                  Cambiar Contraseña
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// ============ Profile Preview Tab Component ============
const ProfilePreviewTab = ({ press }) => {
  const socialPlatforms = {
    instagram: { icon: Instagram, color: 'text-pink-400', bg: 'bg-pink-500/20' },
    tiktok: { icon: Music, color: 'text-zinc-300', bg: 'bg-zinc-500/20' },
    twitter: { icon: Twitter, color: 'text-blue-400', bg: 'bg-blue-500/20' },
    facebook: { icon: Facebook, color: 'text-blue-500', bg: 'bg-blue-600/20' },
    youtube: { icon: Youtube, color: 'text-red-400', bg: 'bg-red-500/20' },
    website: { icon: Globe, color: 'text-green-400', bg: 'bg-green-500/20' }
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const SERVICE_CATEGORIES_MAP = {
    influencer: 'Influencer / Creador de Contenido',
    photographer: 'Fotógrafo/a',
    videographer: 'Camarógrafo/a / Videógrafo',
    digital_media: 'Medio Digital / Blog',
    journalist: 'Periodista / Comunicador',
    radio: 'Radio / Podcast',
    dj_producer: 'DJ / Productor Musical',
    social_media_manager: 'Community Manager',
    graphic_designer: 'Diseñador/a Gráfico',
    event_coverage: 'Cobertura de Eventos',
    marketing: 'Marketing / Publicidad',
    other: 'Otro'
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-white mb-2">Vista Previa del Perfil</h1>
          <p className="text-zinc-400">Así es como las productoras ven tu perfil</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-zinc-500 bg-zinc-900/50 px-3 py-2 rounded-lg border border-zinc-800">
          <Eye size={14} />
          <span>Vista de Productora</span>
        </div>
      </div>

      {/* Preview Card - mimicking how producers see press profiles */}
      <div className="bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-800 border border-zinc-700 rounded-3xl overflow-hidden shadow-2xl">
        {/* Banner */}
        <div className="h-40 relative overflow-hidden">
          {press?.banner_url || press?.cover_image_url ? (
            <img 
              src={press.banner_url || press.cover_image_url} 
              alt="Banner" 
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-r from-blue-600/30 via-purple-600/30 to-pink-600/30" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-transparent to-transparent" />
        </div>

        {/* Profile Header */}
        <div className="px-6 pb-6 -mt-16 relative">
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            {/* Logo */}
            <div className="w-28 h-28 rounded-2xl bg-zinc-800 border-4 border-zinc-900 overflow-hidden shadow-xl flex-shrink-0">
              {press?.logo_url ? (
                <img src={press.logo_url} alt={press.media_name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Newspaper className="text-zinc-600" size={40} />
                </div>
              )}
            </div>

            {/* Name and Categories */}
            <div className="flex-1 pb-2">
              <h2 className="text-2xl font-black text-white mb-1">{press?.media_name || 'Sin nombre'}</h2>
              <div className="flex flex-wrap gap-2 mt-2">
                {press?.service_categories?.length > 0 ? (
                  press.service_categories.slice(0, 3).map((cat) => (
                    <span key={cat} className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs border border-blue-500/30">
                      {SERVICE_CATEGORIES_MAP[cat] || cat}
                    </span>
                  ))
                ) : (
                  <span className="text-zinc-500 text-sm">Sin categorías definidas</span>
                )}
                {press?.service_categories?.length > 3 && (
                  <span className="px-2 py-1 bg-zinc-700/50 text-zinc-400 rounded-full text-xs">
                    +{press.service_categories.length - 3} más
                  </span>
                )}
              </div>
            </div>

            {/* Stats Quick View */}
            <div className="flex gap-4 md:gap-6 pb-2">
              <div className="text-center">
                <p className="text-2xl font-black text-white">{formatNumber(press?.statistics?.monthly_reach || 0)}</p>
                <p className="text-zinc-500 text-xs">Alcance Mensual</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-black text-green-400">{formatNumber(
                  press?.social_networks?.reduce((sum, n) => sum + (n.followers || 0), 0) || 0
                )}</p>
                <p className="text-zinc-500 text-xs">Seguidores</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="p-6 border-t border-zinc-800">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              {/* About */}
              <div>
                <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                  <FileText size={18} className="text-blue-400" />
                  Descripción
                </h3>
                {press?.description ? (
                  <p className="text-zinc-300 text-sm leading-relaxed">{press.description}</p>
                ) : (
                  <p className="text-zinc-500 text-sm italic">Sin descripción. Añade una descripción en Configuración.</p>
                )}
              </div>

              {/* Contact */}
              <div>
                <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                  <Users size={18} className="text-green-400" />
                  Contacto
                </h3>
                <div className="space-y-2 text-sm">
                  {press?.contact_name && (
                    <p className="text-zinc-300">
                      <span className="text-zinc-500">Contacto:</span> {press.contact_name}
                    </p>
                  )}
                  {press?.city && (
                    <p className="text-zinc-300 flex items-center gap-2">
                      <MapPin size={14} className="text-zinc-500" />
                      {press.city}{press?.province ? `, ${press.province}` : ''}
                    </p>
                  )}
                </div>
              </div>

              {/* Social Networks */}
              <div>
                <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                  <LinkIcon size={18} className="text-purple-400" />
                  Redes Sociales
                </h3>
                {press?.social_networks?.length > 0 ? (
                  <div className="flex flex-wrap gap-3">
                    {press.social_networks.map((network, index) => {
                      const platform = socialPlatforms[network.platform] || socialPlatforms.website;
                      const Icon = platform.icon;
                      return (
                        <a
                          key={index}
                          href={network.url.startsWith('http') ? network.url : `https://${network.url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`flex items-center gap-2 px-3 py-2 rounded-xl ${platform.bg} border border-zinc-700 hover:border-zinc-600 transition-colors`}
                        >
                          <Icon size={16} className={platform.color} />
                          <span className={`text-sm ${platform.color}`}>{formatNumber(network.followers)}</span>
                        </a>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-zinc-500 text-sm italic">Sin redes sociales configuradas</p>
                )}
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Audience */}
              <div>
                <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                  <Users size={18} className="text-orange-400" />
                  Audiencia / Target
                </h3>
                {press?.audience && (Object.keys(press.audience).length > 0) ? (
                  <div className="space-y-3">
                    {/* Demographics */}
                    <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-zinc-500 text-xs mb-1">Rango de Edad</p>
                          <p className="text-white font-medium">
                            {press.audience.age_range_min || 18} - {press.audience.age_range_max || 35} años
                          </p>
                        </div>
                        <div>
                          <p className="text-zinc-500 text-xs mb-1">Género</p>
                          <p className="text-white font-medium capitalize">
                            {press.audience.gender_distribution === 'male' ? 'Masculino' :
                             press.audience.gender_distribution === 'female' ? 'Femenino' : 'Mixto'}
                          </p>
                        </div>
                        {press.audience.education_level && (
                          <div>
                            <p className="text-zinc-500 text-xs mb-1">Educación</p>
                            <p className="text-white font-medium capitalize">{press.audience.education_level}</p>
                          </div>
                        )}
                        {press.audience.income_level && (
                          <div>
                            <p className="text-zinc-500 text-xs mb-1">Nivel Económico</p>
                            <p className="text-white font-medium capitalize">{press.audience.income_level}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Interests */}
                    {press.audience.interests?.length > 0 && (
                      <div>
                        <p className="text-zinc-500 text-xs mb-2">Intereses</p>
                        <div className="flex flex-wrap gap-2">
                          {press.audience.interests.map((interest, i) => (
                            <span key={i} className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded-lg text-xs border border-purple-500/30">
                              {interest}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Lifestyle */}
                    {press.audience.lifestyle?.length > 0 && (
                      <div>
                        <p className="text-zinc-500 text-xs mb-2">Estilo de Vida</p>
                        <div className="flex flex-wrap gap-2">
                          {press.audience.lifestyle.map((ls, i) => (
                            <span key={i} className="px-2 py-1 bg-orange-500/20 text-orange-400 rounded-lg text-xs border border-orange-500/30">
                              {ls}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Locations */}
                    {press.audience.locations?.length > 0 && (
                      <div>
                        <p className="text-zinc-500 text-xs mb-2">Ubicaciones</p>
                        <div className="flex flex-wrap gap-2">
                          {press.audience.locations.map((loc, i) => (
                            <span key={i} className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-lg text-xs border border-yellow-500/30">
                              {loc}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-zinc-500 text-sm italic">Sin datos de audiencia configurados. Completá esta información en Configuración &gt; Target/Audiencia.</p>
                )}
              </div>

              {/* Service Packages Preview */}
              <div>
                <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                  <Package size={18} className="text-cyan-400" />
                  Paquetes de Servicios
                </h3>
                {press?.service_packages?.length > 0 ? (
                  <div className="space-y-2">
                    {press.service_packages.filter(p => p.is_active !== false).slice(0, 3).map((pkg, i) => (
                      <div key={i} className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="text-white font-bold text-sm">{pkg.name}</h4>
                            {pkg.description && (
                              <p className="text-zinc-400 text-xs mt-1 line-clamp-1">{pkg.description}</p>
                            )}
                          </div>
                          <span className="text-emerald-400 font-bold">${pkg.price?.toLocaleString()}</span>
                        </div>
                      </div>
                    ))}
                    {press.service_packages.filter(p => p.is_active !== false).length > 3 && (
                      <p className="text-zinc-500 text-xs text-center">
                        +{press.service_packages.filter(p => p.is_active !== false).length - 3} paquetes más
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-zinc-500 text-sm italic">Sin paquetes de servicios. Creá tus paquetes en la sección Mis Servicios.</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Portfolio Preview */}
        {press?.portfolio?.length > 0 && (
          <div className="p-6 border-t border-zinc-800">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Image size={18} className="text-pink-400" />
              Portfolio
            </h3>
            <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
              {press.portfolio.slice(0, 8).map((item, i) => (
                <div key={i} className="aspect-square rounded-xl overflow-hidden bg-zinc-800">
                  <img 
                    src={item.image_url} 
                    alt={item.title || 'Portfolio'} 
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Tips */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
        <h4 className="text-blue-400 font-bold text-sm flex items-center gap-2 mb-2">
          <Sparkles size={16} />
          Tips para mejorar tu perfil
        </h4>
        <ul className="text-zinc-400 text-sm space-y-1">
          {!press?.logo_url && <li>• Subí un logo para que tu perfil se vea más profesional</li>}
          {!press?.banner_url && <li>• Añadí un banner para destacar tu marca</li>}
          {!press?.description && <li>• Escribí una descripción atractiva de tu medio</li>}
          {(!press?.social_networks || press.social_networks.length === 0) && <li>• Añadí tus redes sociales para mostrar tu alcance</li>}
          {(!press?.service_packages || press.service_packages.length === 0) && <li>• Creá paquetes de servicios para que las productoras puedan contratarte</li>}
          {(!press?.audience || Object.keys(press.audience).length === 0) && <li>• Completá los datos de tu audiencia para atraer productoras relevantes</li>}
          {(!press?.portfolio || press.portfolio.length === 0) && <li>• Subí trabajos a tu portfolio para mostrar tu experiencia</li>}
          {press?.logo_url && press?.banner_url && press?.description && press?.social_networks?.length > 0 && 
           press?.service_packages?.length > 0 && press?.audience && press?.portfolio?.length > 0 && (
            <li className="text-green-400">✓ ¡Tu perfil está completo! Las productoras podrán encontrarte fácilmente.</li>
          )}
        </ul>
      </div>
    </motion.div>
  );
};

// ============ DJs Tab Component ============
const DJsTab = () => {
  const [loading, setLoading] = useState(true);
  const [djs, setDjs] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ genre: '', city: '', search: '' });
  const [favorites, setFavorites] = useState(new Set());
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [availableFilters, setAvailableFilters] = useState({ genres: [], cities: [] });
  const [selectedDJ, setSelectedDJ] = useState(null);

  useEffect(() => {
    fetchDJs();
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      const response = await api.get('/press/djs/favorites');
      const favIds = (response.data.favorites || []).map(f => f.dj_id);
      setFavorites(new Set(favIds));
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  const toggleFavorite = async (djId) => {
    const isFav = favorites.has(djId);
    try {
      if (isFav) {
        await api.delete(`/press/djs/favorites/${djId}`);
        setFavorites(prev => {
          const next = new Set(prev);
          next.delete(djId);
          return next;
        });
        toast.success('DJ eliminado de favoritos');
      } else {
        await api.post(`/press/djs/favorites/${djId}`);
        setFavorites(prev => new Set([...prev, djId]));
        toast.success('DJ agregado a favoritos');
      }
    } catch (error) {
      toast.error('Error al actualizar favoritos');
    }
  };

  const fetchDJs = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.genre) params.append('genre', filters.genre);
      if (filters.city) params.append('city', filters.city);
      if (filters.search) params.append('search', filters.search);
      
      const response = await api.get(`/press/djs/browse?${params.toString()}`);
      setDjs(response.data.djs || []);
      setTotal(response.data.total || 0);
      setAvailableFilters(response.data.filters || { genres: [], cities: [] });
    } catch (error) {
      console.error('Error fetching DJs:', error);
      toast.error('Error al cargar DJs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchDJs();
    }, 300);
    return () => clearTimeout(timer);
  }, [filters]);

  const getFlag = (code) => {
    const flags = {
      'AR': '🇦🇷', 'BR': '🇧🇷', 'CL': '🇨🇱', 'CO': '🇨🇴', 'MX': '🇲🇽',
      'PE': '🇵🇪', 'UY': '🇺🇾', 'ES': '🇪🇸', 'US': '🇺🇸', 'OTHER': '🌎'
    };
    return flags[code] || null;
  };

  const displayDJs = showFavoritesOnly 
    ? djs.filter(dj => favorites.has(dj.id))
    : djs;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-white mb-2">DJs Disponibles</h1>
          <p className="text-zinc-400">Descubrí y contactá DJs para tus eventos</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={showFavoritesOnly ? "default" : "outline"}
            onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
            className={showFavoritesOnly ? "bg-red-500 hover:bg-red-600" : ""}
          >
            <Heart size={16} className="mr-2" fill={showFavoritesOnly ? "currentColor" : "none"} />
            Favoritos ({favorites.size})
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                placeholder="Buscar por nombre..."
                className="bg-zinc-800 border-zinc-700 pl-10"
              />
            </div>
          </div>
          <Select value={filters.genre || "all"} onValueChange={(v) => setFilters({ ...filters, genre: v === "all" ? '' : v })}>
            <SelectTrigger className="bg-zinc-800 border-zinc-700">
              <SelectValue placeholder="Género musical" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700">
              <SelectItem value="all">Todos los géneros</SelectItem>
              {(availableFilters.genres || []).map(g => (
                <SelectItem key={g} value={g}>{g}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.city || "all"} onValueChange={(v) => setFilters({ ...filters, city: v === "all" ? '' : v })}>
            <SelectTrigger className="bg-zinc-800 border-zinc-700">
              <SelectValue placeholder="Ciudad" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700">
              <SelectItem value="all">Todas las ciudades</SelectItem>
              {(availableFilters.cities || []).map(c => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin text-violet-500" size={32} />
        </div>
      ) : displayDJs.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900/30 rounded-xl border border-zinc-800">
          <Disc3 size={48} className="mx-auto text-zinc-600 mb-4" />
          <h3 className="text-lg font-bold text-white mb-2">
            {showFavoritesOnly ? 'No tenés DJs favoritos' : 'No se encontraron DJs'}
          </h3>
          <p className="text-zinc-400">
            {showFavoritesOnly ? 'Agregá DJs a favoritos para verlos aquí' : 'Probá con otros filtros'}
          </p>
        </div>
      ) : (
        <>
          <p className="text-sm text-zinc-500">{displayDJs.length} DJ(s) encontrado(s)</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayDJs.map(dj => (
              <motion.div
                key={dj.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.02 }}
                className="bg-zinc-900/50 border border-zinc-800 rounded-2xl overflow-hidden cursor-pointer hover:border-violet-500/30 transition-all group"
                onClick={() => setSelectedDJ(dj)}
              >
                {/* Banner */}
                <div className="h-28 bg-gradient-to-br from-violet-500/20 to-transparent relative">
                  {dj.banner_url && (
                    <img src={dj.banner_url} alt="" className="w-full h-full object-cover" />
                  )}
                  <div className="absolute -bottom-6 left-4">
                    <div className="w-14 h-14 rounded-xl border-4 border-zinc-900 overflow-hidden bg-zinc-800 relative">
                      {dj.photo_url ? (
                        <img src={dj.photo_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Disc3 size={20} className="text-violet-500" />
                        </div>
                      )}
                      {dj.nationality && getFlag(dj.nationality) && (
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-black/80 flex items-center justify-center text-xs">
                          {getFlag(dj.nationality)}
                        </div>
                      )}
                    </div>
                  </div>
                  {dj.featured && (
                    <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 bg-violet-500 rounded text-black text-xs font-bold">
                      <Star size={10} /> Destacado
                    </div>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleFavorite(dj.id); }}
                    className={`absolute top-2 ${dj.featured ? 'right-24' : 'right-2'} p-1.5 rounded-lg transition-all ${
                      favorites.has(dj.id) 
                        ? 'bg-red-500 text-white' 
                        : 'bg-black/50 text-white/60 hover:text-white'
                    }`}
                  >
                    <Heart size={14} fill={favorites.has(dj.id) ? 'currentColor' : 'none'} />
                  </button>
                </div>
                
                <div className="pt-8 pb-4 px-4">
                  <h3 className="font-bold text-base mb-1 text-white">{dj.artistic_name}</h3>
                  <p className="text-xs text-zinc-400 flex items-center gap-1 mb-3">
                    <MapPin size={10} /> {dj.city}
                    {dj.management?.name && (
                      <span className="text-purple-400 ml-2 flex items-center gap-1">
                        <Building2 size={10} /> {dj.management.name}
                      </span>
                    )}
                  </p>
                  
                  {/* Genres */}
                  <div className="flex flex-wrap gap-1 mb-3">
                    {(dj.genres || []).slice(0, 3).map(genre => (
                      <span key={genre} className="px-2 py-0.5 bg-violet-500/10 text-violet-400 rounded text-xs">
                        {genre}
                      </span>
                    ))}
                  </div>
                  
                  {/* Quick Stats */}
                  <div className="flex items-center justify-between text-xs text-zinc-500 pt-3 border-t border-zinc-800">
                    <span className="flex items-center gap-1">
                      <Clock size={10} /> {dj.experience_years || '?'} años
                    </span>
                    {dj.cache_min && (
                      <span className="flex items-center gap-1">
                        <DollarSign size={10} /> ${dj.cache_min?.toLocaleString()}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Eye size={10} /> {dj.profile_views || 0}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </>
      )}

      {/* DJ Detail Modal */}
      <Dialog open={!!selectedDJ} onOpenChange={() => setSelectedDJ(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-4xl max-h-[90vh] overflow-y-auto p-0">
          {selectedDJ && (
            <>
              {/* Banner Header */}
              <div className="relative h-48 bg-gradient-to-br from-violet-600/30 to-purple-900/30">
                {selectedDJ.banner_url && (
                  <img src={selectedDJ.banner_url} alt="" className="w-full h-full object-cover" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/50 to-transparent" />
                
                {/* Profile Photo */}
                <div className="absolute -bottom-12 left-6">
                  <div className="w-28 h-28 rounded-2xl border-4 border-zinc-950 overflow-hidden bg-zinc-800 shadow-xl">
                    {selectedDJ.photo_url ? (
                      <img src={selectedDJ.photo_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Disc3 size={40} className="text-violet-500" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Featured Badge */}
                {selectedDJ.featured && (
                  <div className="absolute top-4 right-4 flex items-center gap-1 px-3 py-1.5 bg-violet-500 rounded-lg text-black text-sm font-bold">
                    <Star size={14} /> Destacado
                  </div>
                )}

                {/* Favorite Button */}
                <button
                  onClick={() => toggleFavorite(selectedDJ.id)}
                  className={`absolute top-4 ${selectedDJ.featured ? 'right-32' : 'right-4'} p-2 rounded-lg transition-all ${
                    favorites.has(selectedDJ.id) 
                      ? 'bg-red-500 text-white' 
                      : 'bg-black/50 text-white/80 hover:text-white'
                  }`}
                >
                  <Heart size={18} fill={favorites.has(selectedDJ.id) ? 'currentColor' : 'none'} />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 pt-16 pb-6 space-y-6">
                {/* Header Info */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                  <div>
                    <h2 className="text-3xl font-black text-white">{selectedDJ.artistic_name}</h2>
                    {selectedDJ.real_name && (
                      <p className="text-zinc-400 text-sm">{selectedDJ.real_name}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-zinc-400">
                      <span className="flex items-center gap-1">
                        <MapPin size={14} /> {selectedDJ.city}
                      </span>
                      {selectedDJ.experience_years && (
                        <span className="flex items-center gap-1">
                          <Clock size={14} /> {selectedDJ.experience_years} años de experiencia
                        </span>
                      )}
                      {selectedDJ.travel_available && (
                        <span className="flex items-center gap-1 text-green-400">
                          <Globe size={14} /> Disponible para viajar
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Price Range */}
                  {(selectedDJ.cache_min || selectedDJ.cache_max) && (
                    <div className="bg-zinc-800/50 rounded-xl p-4 text-right">
                      <p className="text-xs text-zinc-500 mb-1">Caché</p>
                      <p className="text-2xl font-black text-green-400">
                        ${selectedDJ.cache_min?.toLocaleString()}
                        {selectedDJ.cache_max && ` - $${selectedDJ.cache_max.toLocaleString()}`}
                      </p>
                      <p className="text-xs text-zinc-500">{selectedDJ.cache_currency || 'ARS'}</p>
                    </div>
                  )}
                </div>

                {/* Genres */}
                <div className="flex flex-wrap gap-2">
                  {(selectedDJ.genres || []).map(genre => (
                    <span key={genre} className="px-3 py-1.5 bg-violet-500/20 text-violet-400 rounded-lg text-sm border border-violet-500/30">
                      {genre}
                    </span>
                  ))}
                </div>

                {/* Bio */}
                {selectedDJ.bio && (
                  <div>
                    <h4 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                      <FileText size={16} className="text-violet-400" /> Biografía
                    </h4>
                    <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-line">{selectedDJ.bio}</p>
                  </div>
                )}

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-zinc-800/50 rounded-xl p-4 text-center border border-zinc-700">
                    <p className="text-2xl font-black text-white">{selectedDJ.experience_years || '-'}</p>
                    <p className="text-xs text-zinc-500">Años Exp.</p>
                  </div>
                  <div className="bg-zinc-800/50 rounded-xl p-4 text-center border border-zinc-700">
                    <p className="text-2xl font-black text-violet-400">{selectedDJ.profile_views || 0}</p>
                    <p className="text-xs text-zinc-500">Vistas</p>
                  </div>
                  {selectedDJ.set_duration_min && (
                    <div className="bg-zinc-800/50 rounded-xl p-4 text-center border border-zinc-700">
                      <p className="text-2xl font-black text-cyan-400">
                        {selectedDJ.set_duration_min}{selectedDJ.set_duration_max && `-${selectedDJ.set_duration_max}`}h
                      </p>
                      <p className="text-xs text-zinc-500">Duración Set</p>
                    </div>
                  )}
                  {selectedDJ.equipment_own && (
                    <div className="bg-zinc-800/50 rounded-xl p-4 text-center border border-zinc-700">
                      <p className="text-xl font-black text-green-400">✓</p>
                      <p className="text-xs text-zinc-500">Equipo Propio</p>
                    </div>
                  )}
                </div>

                {/* Performance Style & Special Skills */}
                {(selectedDJ.performance_style || selectedDJ.special_skills) && (
                  <div className="grid md:grid-cols-2 gap-4">
                    {selectedDJ.performance_style && (
                      <div>
                        <h4 className="text-sm font-bold text-white mb-2">Estilo de Performance</h4>
                        <p className="text-zinc-400 text-sm">{selectedDJ.performance_style}</p>
                      </div>
                    )}
                    {selectedDJ.special_skills && (
                      <div>
                        <h4 className="text-sm font-bold text-white mb-2">Habilidades Especiales</h4>
                        <p className="text-zinc-400 text-sm">{selectedDJ.special_skills}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Equipment Details */}
                {selectedDJ.equipment_details && (
                  <div>
                    <h4 className="text-sm font-bold text-white mb-2">Equipamiento</h4>
                    <p className="text-zinc-400 text-sm">{selectedDJ.equipment_details}</p>
                  </div>
                )}

                {/* Releases / Labels */}
                {((selectedDJ.releases && selectedDJ.releases.length > 0) || (selectedDJ.labels && selectedDJ.labels.length > 0)) && (
                  <div className="grid md:grid-cols-2 gap-4">
                    {selectedDJ.labels && selectedDJ.labels.length > 0 && (
                      <div>
                        <h4 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                          <Award size={16} className="text-yellow-400" /> Sellos Discográficos
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedDJ.labels.map((label, i) => (
                            <span key={i} className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-lg text-xs">
                              {typeof label === 'string' ? label : label.name || label.title || 'Label'}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedDJ.releases && selectedDJ.releases.length > 0 && (
                      <div>
                        <h4 className="text-sm font-bold text-white mb-2">Releases</h4>
                        <div className="space-y-2">
                          {selectedDJ.releases.slice(0, 5).map((release, i) => (
                            <div key={i} className="text-sm">
                              {typeof release === 'string' ? (
                                <span className="text-zinc-400">{release}</span>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <span className="text-white">{release.title}</span>
                                  {release.year && <span className="text-zinc-500">({release.year})</span>}
                                  {release.link && (
                                    <a href={release.link} target="_blank" rel="noopener noreferrer" 
                                      className="text-violet-400 hover:text-violet-300">
                                      <ExternalLink size={12} />
                                    </a>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Festivals & Residencies */}
                {((selectedDJ.festivals && selectedDJ.festivals.length > 0) || (selectedDJ.residencies && selectedDJ.residencies.length > 0)) && (
                  <div className="grid md:grid-cols-2 gap-4">
                    {selectedDJ.festivals && selectedDJ.festivals.length > 0 && (
                      <div>
                        <h4 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                          <Star size={16} className="text-orange-400" /> Festivales
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedDJ.festivals.map((fest, i) => (
                            <span key={i} className="px-2 py-1 bg-orange-500/20 text-orange-400 rounded-lg text-xs">
                              {typeof fest === 'string' ? fest : fest.name || fest.title || 'Festival'}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedDJ.residencies && selectedDJ.residencies.length > 0 && (
                      <div>
                        <h4 className="text-sm font-bold text-white mb-2">Residencias</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedDJ.residencies.map((res, i) => (
                            <span key={i} className="px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded-lg text-xs">
                              {typeof res === 'string' ? res : res.name || res.venue || 'Residencia'}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Awards */}
                {selectedDJ.awards && selectedDJ.awards.length > 0 && (
                  <div>
                    <h4 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                      <Award size={16} className="text-yellow-400" /> Premios y Reconocimientos
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedDJ.awards.map((award, i) => (
                        <span key={i} className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-lg text-xs">
                          {typeof award === 'string' ? award : award.name || award.title || 'Premio'}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Gallery */}
                {selectedDJ.gallery_urls && selectedDJ.gallery_urls.length > 0 && (
                  <div>
                    <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                      <Image size={16} className="text-pink-400" /> Galería
                    </h4>
                    <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                      {selectedDJ.gallery_urls.slice(0, 8).map((url, i) => {
                        const imageUrl = typeof url === 'string' ? url : url.url || url.image_url;
                        if (!imageUrl || !imageUrl.startsWith('http')) return null;
                        return (
                          <a key={i} href={imageUrl} target="_blank" rel="noopener noreferrer" 
                            className="aspect-square rounded-xl overflow-hidden bg-zinc-800 hover:opacity-80 transition-opacity">
                            <img src={imageUrl} alt="" className="w-full h-full object-cover" />
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Videos */}
                {selectedDJ.video_urls && selectedDJ.video_urls.length > 0 && (
                  <div>
                    <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                      <Play size={16} className="text-red-400" /> Videos
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedDJ.video_urls.map((url, i) => {
                        const videoUrl = typeof url === 'string' ? url : url.url || url.video_url;
                        if (!videoUrl || !videoUrl.startsWith('http')) return null;
                        return (
                          <a key={i} href={videoUrl} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-2 px-3 py-2 bg-red-500/20 text-red-400 rounded-lg text-sm hover:bg-red-500/30 transition-colors">
                            <Play size={14} /> Video {i + 1}
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Social Links & Music Platforms */}
                <div>
                  <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                    <LinkIcon size={16} className="text-blue-400" /> Redes y Plataformas
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedDJ.instagram && (
                      <a href={selectedDJ.instagram} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 bg-pink-500/20 text-pink-400 rounded-lg text-sm hover:bg-pink-500/30 transition-colors">
                        <Instagram size={16} /> Instagram
                      </a>
                    )}
                    {selectedDJ.facebook && (
                      <a href={selectedDJ.facebook} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 bg-blue-500/20 text-blue-400 rounded-lg text-sm hover:bg-blue-500/30 transition-colors">
                        <Facebook size={16} /> Facebook
                      </a>
                    )}
                    {selectedDJ.twitter && (
                      <a href={selectedDJ.twitter} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 bg-sky-500/20 text-sky-400 rounded-lg text-sm hover:bg-sky-500/30 transition-colors">
                        <Twitter size={16} /> Twitter/X
                      </a>
                    )}
                    {selectedDJ.tiktok && (
                      <a href={selectedDJ.tiktok} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 bg-zinc-500/20 text-zinc-300 rounded-lg text-sm hover:bg-zinc-500/30 transition-colors">
                        <Music size={16} /> TikTok
                      </a>
                    )}
                    {selectedDJ.youtube && (
                      <a href={selectedDJ.youtube} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 bg-red-500/20 text-red-400 rounded-lg text-sm hover:bg-red-500/30 transition-colors">
                        <Youtube size={16} /> YouTube
                      </a>
                    )}
                    {selectedDJ.spotify_url && (
                      <a href={selectedDJ.spotify_url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 bg-green-500/20 text-green-400 rounded-lg text-sm hover:bg-green-500/30 transition-colors">
                        <Music size={16} /> Spotify
                      </a>
                    )}
                    {selectedDJ.soundcloud_url && (
                      <a href={selectedDJ.soundcloud_url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 bg-orange-500/20 text-orange-400 rounded-lg text-sm hover:bg-orange-500/30 transition-colors">
                        <Headphones size={16} /> SoundCloud
                      </a>
                    )}
                    {selectedDJ.mixcloud_url && (
                      <a href={selectedDJ.mixcloud_url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg text-sm hover:bg-cyan-500/30 transition-colors">
                        <Headphones size={16} /> Mixcloud
                      </a>
                    )}
                    {selectedDJ.beatport_url && (
                      <a href={selectedDJ.beatport_url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 bg-teal-500/20 text-teal-400 rounded-lg text-sm hover:bg-teal-500/30 transition-colors">
                        <Music size={16} /> Beatport
                      </a>
                    )}
                  </div>
                </div>

                {/* Technical Rider */}
                {(selectedDJ.technical_rider || selectedDJ.rider_pdf_url) && (
                  <div className="bg-zinc-800/30 rounded-xl p-4 border border-zinc-700">
                    <h4 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                      <FileText size={16} className="text-cyan-400" /> Rider Técnico
                    </h4>
                    {selectedDJ.technical_rider && (
                      <p className="text-zinc-400 text-sm mb-3">{selectedDJ.technical_rider}</p>
                    )}
                    {selectedDJ.rider_pdf_url && (
                      <a href={selectedDJ.rider_pdf_url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg text-sm hover:bg-cyan-500/30 transition-colors">
                        <ExternalLink size={14} /> Descargar Rider PDF
                      </a>
                    )}
                  </div>
                )}

                {/* Contact */}
                <div className="bg-violet-500/10 rounded-xl p-4 border border-violet-500/30">
                  <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                    <MessageSquare size={16} className="text-violet-400" /> Contacto
                  </h4>
                  <div className="flex flex-wrap gap-4">
                    {selectedDJ.email_contact && (
                      <a href={`mailto:${selectedDJ.email_contact}`} 
                        className="flex items-center gap-2 text-sm text-zinc-300 hover:text-white transition-colors">
                        <Mail size={16} className="text-violet-400" /> {selectedDJ.email_contact}
                      </a>
                    )}
                    {selectedDJ.phone && (
                      <a href={`tel:${selectedDJ.phone}`}
                        className="flex items-center gap-2 text-sm text-zinc-300 hover:text-white transition-colors">
                        <Phone size={16} className="text-violet-400" /> {selectedDJ.phone}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

// ============ Jobs Tab Component ============
const JobsTab = ({ press }) => {
  const [loading, setLoading] = useState(true);
  const [postings, setPostings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [postingModalOpen, setPostingModalOpen] = useState(false);
  const [editingPosting, setEditingPosting] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [postingToDelete, setPostingToDelete] = useState(null);
  const [applicationsModalOpen, setApplicationsModalOpen] = useState(false);
  const [selectedPostingForApps, setSelectedPostingForApps] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loadingApplications, setLoadingApplications] = useState(false);
  
  const [postingForm, setPostingForm] = useState({
    title: '',
    description: '',
    category: '',
    location: '',
    job_type: '',
    salary_range: '',
    requirements: [],
    benefits: [],
    contact_email: '',
    contact_phone: '',
    is_anonymous: false
  });
  const [newRequirement, setNewRequirement] = useState('');
  const [newBenefit, setNewBenefit] = useState('');

  const JOB_TYPES = ['Tiempo completo', 'Part-time', 'Por evento', 'Freelance', 'Temporario'];
  const JOB_CATEGORIES = [
    { id: 'rrpp', name: 'RRPP / Promotor' },
    { id: 'security', name: 'Seguridad' },
    { id: 'bar', name: 'Barra / Bartender' },
    { id: 'technical', name: 'Técnico / Sonido / Iluminación' },
    { id: 'production', name: 'Producción' },
    { id: 'photography', name: 'Fotografía / Video' },
    { id: 'design', name: 'Diseño / Marketing' },
    { id: 'admin', name: 'Administrativo' },
    { id: 'other', name: 'Otro' }
  ];

  useEffect(() => {
    fetchPostings();
  }, []);

  const fetchPostings = async () => {
    try {
      const response = await api.get('/press/jobs/postings/my');
      setPostings(response.data.postings || []);
    } catch (error) {
      console.error('Error fetching postings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingPosting(null);
    setPostingForm({
      title: '',
      description: '',
      category: '',
      location: '',
      job_type: '',
      salary_range: '',
      requirements: [],
      benefits: [],
      contact_email: press?.email || '',
      contact_phone: press?.phone || '',
      is_anonymous: false
    });
    setPostingModalOpen(true);
  };

  const handleOpenEdit = (posting) => {
    setEditingPosting(posting);
    setPostingForm({
      title: posting.title,
      description: posting.description,
      category: posting.category,
      location: posting.location,
      job_type: posting.job_type,
      salary_range: posting.salary_range || '',
      requirements: posting.requirements || [],
      benefits: posting.benefits || [],
      contact_email: posting.contact_email || '',
      contact_phone: posting.contact_phone || '',
      is_anonymous: posting.is_anonymous || false
    });
    setPostingModalOpen(true);
  };

  const handleSubmitPosting = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      if (editingPosting) {
        await api.put(`/press/jobs/postings/${editingPosting.id}`, postingForm);
        toast.success('Empleo actualizado');
      } else {
        await api.post('/press/jobs/postings', postingForm);
        toast.success('Empleo publicado');
      }
      setPostingModalOpen(false);
      fetchPostings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePosting = async () => {
    if (!postingToDelete) return;
    try {
      await api.delete(`/press/jobs/postings/${postingToDelete.id}`);
      toast.success('Empleo eliminado');
      setDeleteDialogOpen(false);
      setPostingToDelete(null);
      fetchPostings();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const togglePostingActive = async (posting) => {
    try {
      await api.put(`/press/jobs/postings/${posting.id}`, { is_active: !posting.is_active });
      toast.success(posting.is_active ? 'Empleo pausado' : 'Empleo activado');
      fetchPostings();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const fetchApplications = async (postingId) => {
    setLoadingApplications(true);
    try {
      const response = await api.get(`/press/jobs/postings/${postingId}/applications`);
      setApplications(response.data.applications || []);
    } catch (error) {
      toast.error('Error al cargar aplicantes');
    } finally {
      setLoadingApplications(false);
    }
  };

  const handleViewApplications = (posting) => {
    setSelectedPostingForApps(posting);
    fetchApplications(posting.id);
    setApplicationsModalOpen(true);
  };

  const addRequirement = () => {
    if (newRequirement.trim()) {
      setPostingForm({ ...postingForm, requirements: [...postingForm.requirements, newRequirement.trim()] });
      setNewRequirement('');
    }
  };

  const addBenefit = () => {
    if (newBenefit.trim()) {
      setPostingForm({ ...postingForm, benefits: [...postingForm.benefits, newBenefit.trim()] });
      setNewBenefit('');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-white mb-2">Portal de Empleo</h1>
          <p className="text-zinc-400">Publicá ofertas de trabajo y encontrá talentos</p>
        </div>
        <Button onClick={handleOpenCreate} className="bg-violet-600 hover:bg-violet-700">
          <Plus size={16} className="mr-2" />
          Crear Empleo
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
          <p className="text-2xl font-black text-white">{postings.length}</p>
          <p className="text-xs text-zinc-500">Empleos Publicados</p>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
          <p className="text-2xl font-black text-green-400">{postings.filter(p => p.is_active).length}</p>
          <p className="text-xs text-zinc-500">Activos</p>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
          <p className="text-2xl font-black text-violet-400">{postings.reduce((sum, p) => sum + (p.views || 0), 0)}</p>
          <p className="text-xs text-zinc-500">Vistas Totales</p>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
          <p className="text-2xl font-black text-orange-400">{postings.reduce((sum, p) => sum + (p.applications_count || 0), 0)}</p>
          <p className="text-xs text-zinc-500">Postulaciones</p>
        </div>
      </div>

      {/* Postings List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin text-violet-500" size={32} />
        </div>
      ) : postings.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900/30 rounded-xl border border-zinc-800">
          <Briefcase size={48} className="mx-auto text-zinc-600 mb-4" />
          <h3 className="text-lg font-bold text-white mb-2">No tenés empleos publicados</h3>
          <p className="text-zinc-400 mb-4">Creá tu primera oferta de trabajo</p>
          <Button onClick={handleOpenCreate} className="bg-violet-600 hover:bg-violet-700">
            <Plus size={16} className="mr-2" />
            Crear Empleo
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {postings.map(posting => (
            <div
              key={posting.id}
              className={`bg-zinc-900/50 border rounded-xl p-5 transition-all ${
                posting.is_active ? 'border-zinc-800 hover:border-violet-500/30' : 'border-zinc-800/50 opacity-60'
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-lg text-white">{posting.title}</h3>
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      posting.is_active ? 'bg-green-500/20 text-green-400' : 'bg-zinc-700 text-zinc-400'
                    }`}>
                      {posting.is_active ? 'Activo' : 'Pausado'}
                    </span>
                    {posting.is_anonymous && (
                      <span className="px-2 py-0.5 rounded text-xs bg-purple-500/20 text-purple-400 flex items-center gap-1">
                        <EyeOff size={10} /> Anónimo
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs text-zinc-400 mb-2">
                    <span className="flex items-center gap-1">
                      <MapPin size={12} /> {posting.location || 'Sin ubicación'}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={12} /> {posting.job_type}
                    </span>
                    {posting.salary_range && (
                      <span className="flex items-center gap-1">
                        <DollarSign size={12} /> {posting.salary_range}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Eye size={12} /> {posting.views || 0} vistas
                    </span>
                    <span className="flex items-center gap-1">
                      <Users size={12} /> {posting.applications_count || 0} postulantes
                    </span>
                  </div>
                  {posting.description && (
                    <p className="text-sm text-zinc-400 line-clamp-2">{posting.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewApplications(posting)}
                    className="text-xs"
                  >
                    <Users size={14} className="mr-1" />
                    Ver Postulantes
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => togglePostingActive(posting)}
                  >
                    {posting.is_active ? <EyeOff size={14} /> : <Eye size={14} />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleOpenEdit(posting)}
                  >
                    <Edit2 size={14} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-400 hover:text-red-300"
                    onClick={() => { setPostingToDelete(posting); setDeleteDialogOpen(true); }}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={postingModalOpen} onOpenChange={setPostingModalOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingPosting ? 'Editar Empleo' : 'Crear Nuevo Empleo'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitPosting} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-zinc-400">Título del puesto *</Label>
                <Input
                  value={postingForm.title}
                  onChange={(e) => setPostingForm({ ...postingForm, title: e.target.value })}
                  placeholder="Ej: Fotógrafo para eventos"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                  required
                />
              </div>
              <div>
                <Label className="text-zinc-400">Categoría *</Label>
                <Select 
                  value={postingForm.category} 
                  onValueChange={(v) => setPostingForm({ ...postingForm, category: v })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    {JOB_CATEGORIES.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400">Tipo de empleo *</Label>
                <Select 
                  value={postingForm.job_type} 
                  onValueChange={(v) => setPostingForm({ ...postingForm, job_type: v })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    {JOB_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400">Ubicación *</Label>
                <Input
                  value={postingForm.location}
                  onChange={(e) => setPostingForm({ ...postingForm, location: e.target.value })}
                  placeholder="Ej: CABA, Buenos Aires"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                  required
                />
              </div>
              <div>
                <Label className="text-zinc-400">Rango salarial</Label>
                <Input
                  value={postingForm.salary_range}
                  onChange={(e) => setPostingForm({ ...postingForm, salary_range: e.target.value })}
                  placeholder="Ej: $50.000 - $80.000"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-zinc-400">Descripción *</Label>
              <Textarea
                value={postingForm.description}
                onChange={(e) => setPostingForm({ ...postingForm, description: e.target.value })}
                placeholder="Describí el puesto, responsabilidades y lo que buscás..."
                className="bg-zinc-800 border-zinc-700 mt-1 min-h-[100px]"
                required
              />
            </div>

            {/* Requirements */}
            <div>
              <Label className="text-zinc-400">Requisitos</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={newRequirement}
                  onChange={(e) => setNewRequirement(e.target.value)}
                  placeholder="Agregar requisito..."
                  className="bg-zinc-800 border-zinc-700"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addRequirement())}
                />
                <Button type="button" variant="outline" onClick={addRequirement}>
                  <Plus size={16} />
                </Button>
              </div>
              {postingForm.requirements.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {postingForm.requirements.map((req, i) => (
                    <span key={i} className="px-2 py-1 bg-zinc-800 rounded-lg text-sm flex items-center gap-2">
                      {req}
                      <button
                        type="button"
                        onClick={() => setPostingForm({
                          ...postingForm,
                          requirements: postingForm.requirements.filter((_, idx) => idx !== i)
                        })}
                        className="text-zinc-500 hover:text-white"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Benefits */}
            <div>
              <Label className="text-zinc-400">Beneficios</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={newBenefit}
                  onChange={(e) => setNewBenefit(e.target.value)}
                  placeholder="Agregar beneficio..."
                  className="bg-zinc-800 border-zinc-700"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addBenefit())}
                />
                <Button type="button" variant="outline" onClick={addBenefit}>
                  <Plus size={16} />
                </Button>
              </div>
              {postingForm.benefits.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {postingForm.benefits.map((ben, i) => (
                    <span key={i} className="px-2 py-1 bg-green-500/20 text-green-400 rounded-lg text-sm flex items-center gap-2">
                      {ben}
                      <button
                        type="button"
                        onClick={() => setPostingForm({
                          ...postingForm,
                          benefits: postingForm.benefits.filter((_, idx) => idx !== i)
                        })}
                        className="text-green-300 hover:text-white"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Contact */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400">Email de contacto</Label>
                <Input
                  value={postingForm.contact_email}
                  onChange={(e) => setPostingForm({ ...postingForm, contact_email: e.target.value })}
                  placeholder="contacto@ejemplo.com"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Teléfono de contacto</Label>
                <Input
                  value={postingForm.contact_phone}
                  onChange={(e) => setPostingForm({ ...postingForm, contact_phone: e.target.value })}
                  placeholder="+54 11..."
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
            </div>

            {/* Anonymous Option */}
            <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="is_anonymous"
                  checked={postingForm.is_anonymous}
                  onChange={(e) => setPostingForm({ ...postingForm, is_anonymous: e.target.checked })}
                  className="mt-1 w-4 h-4 rounded border-zinc-600 bg-zinc-700 text-violet-500 focus:ring-violet-500"
                />
                <div>
                  <Label htmlFor="is_anonymous" className="text-white font-bold cursor-pointer">
                    Publicar como anónimo
                  </Label>
                  <p className="text-zinc-400 text-sm mt-1">
                    Tu nombre, logo y datos de contacto no serán visibles. Los postulantes solo podrán aplicar a través de la plataforma.
                  </p>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPostingModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting} className="bg-violet-600 hover:bg-violet-700">
                {submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                {editingPosting ? 'Guardar Cambios' : 'Publicar Empleo'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Applications Modal */}
      <Dialog open={applicationsModalOpen} onOpenChange={setApplicationsModalOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              Postulantes - {selectedPostingForApps?.title}
            </DialogTitle>
          </DialogHeader>
          {loadingApplications ? (
            <div className="flex justify-center py-8">
              <Loader2 className="animate-spin text-violet-500" size={32} />
            </div>
          ) : applications.length === 0 ? (
            <div className="text-center py-8">
              <Users size={32} className="mx-auto text-zinc-600 mb-2" />
              <p className="text-zinc-400">No hay postulantes aún</p>
            </div>
          ) : (
            <div className="space-y-3">
              {applications.map(app => (
                <div key={app.id} className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-bold text-white">{app.applicant_name || 'Anónimo'}</p>
                      {app.applicant_email && (
                        <a href={`mailto:${app.applicant_email}`} className="text-sm text-violet-400 hover:underline">
                          {app.applicant_email}
                        </a>
                      )}
                      {app.phone && (
                        <p className="text-sm text-zinc-400">{app.phone}</p>
                      )}
                    </div>
                    <span className={`px-2 py-1 rounded text-xs ${
                      app.status === 'accepted' ? 'bg-green-500/20 text-green-400' :
                      app.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                      'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {app.status === 'accepted' ? 'Aceptado' : app.status === 'rejected' ? 'Rechazado' : 'Pendiente'}
                    </span>
                  </div>
                  {app.cover_letter && (
                    <p className="text-sm text-zinc-300 mt-2 line-clamp-3">{app.cover_letter}</p>
                  )}
                  {app.portfolio_url && (
                    <a href={app.portfolio_url} target="_blank" rel="noopener noreferrer" 
                      className="text-sm text-violet-400 hover:underline mt-1 inline-block">
                      Ver Portfolio
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">¿Eliminar empleo?</DialogTitle>
          </DialogHeader>
          <p className="text-zinc-400">
            Esta acción no se puede deshacer. Se eliminará "{postingToDelete?.title}".
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDeletePosting}>
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

// ============ Main Dashboard Component ============
const PressDashboardPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [press, setPress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [pressPolicies, setPressPolicies] = useState([]);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [policiesAccepted, setPoliciesAccepted] = useState(true); // NEW: Track policy acceptance
  const [acceptingPolicies, setAcceptingPolicies] = useState(false); // NEW: Loading state
  const [completedWorksCount, setCompletedWorksCount] = useState(0); // Count of completed contracts
  const [navBadges, setNavBadges] = useState({
    services: 0, // pending quotes + active contracts
    events: 0,   // available events
  });
  
  // Editable stats state
  const [editingStats, setEditingStats] = useState(false);
  const [statsForm, setStatsForm] = useState({
    monthly_reach: 0,
    average_story_views: 0
  });
  const [savingStats, setSavingStats] = useState(false);

  // Handle URL tab param for direct navigation to contracts
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'contratos') {
      setActiveTab('events');
    }
  }, [searchParams]);

  useEffect(() => {
    const token = localStorage.getItem('press_token');
    if (!token) {
      navigate('/prensa/login');
      return;
    }

    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    fetchPressData();
    fetchPressPolicies();
    fetchNavBadges();
    checkPolicyAcceptance(); // NEW: Check if policies are accepted
  }, [navigate]);

  // NEW: Check if user has accepted current policies
  const checkPolicyAcceptance = async () => {
    try {
      const response = await api.get('/press/me/policy-acceptance');
      setPoliciesAccepted(response.data.policies_accepted);
    } catch (error) {
      console.error('Error checking policy acceptance:', error);
      // Don't block if check fails
      setPoliciesAccepted(true);
    }
  };

  // NEW: Handle accepting policies
  const handleAcceptPolicies = async () => {
    setAcceptingPolicies(true);
    try {
      await api.post('/press/me/accept-policies');
      setPoliciesAccepted(true);
      toast.success('Políticas aceptadas correctamente');
    } catch (error) {
      toast.error('Error al aceptar las políticas');
    } finally {
      setAcceptingPolicies(false);
    }
  };

  // Handle saving editable stats
  const handleSaveStats = async () => {
    setSavingStats(true);
    try {
      await api.put('/press/me/stats', statsForm);
      toast.success('Estadísticas actualizadas correctamente');
      setEditingStats(false);
      fetchPressData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar estadísticas');
    } finally {
      setSavingStats(false);
    }
  };

  // Initialize stats form when press data loads
  useEffect(() => {
    if (press?.statistics) {
      setStatsForm({
        monthly_reach: press.statistics.monthly_reach || 0,
        average_story_views: press.statistics.average_story_views || 0
      });
    }
  }, [press?.statistics]);

  const fetchNavBadges = async () => {
    try {
      const [quotesRes, contractsRes] = await Promise.all([
        api.get('/press/quotes/press').catch(() => ({ data: [] })),
        api.get('/press/contracts/press').catch(() => ({ data: [] }))
      ]);
      
      const pendingQuotes = Array.isArray(quotesRes.data) 
        ? quotesRes.data.filter(q => q.status === 'pending').length 
        : 0;
      const activeContracts = Array.isArray(contractsRes.data) 
        ? contractsRes.data.filter(c => c.status === 'active' || c.status === 'in_progress').length 
        : 0;
      
      // Count completed contracts for "Trabajos Realizados" stat
      const completedContracts = Array.isArray(contractsRes.data) 
        ? contractsRes.data.filter(c => c.status === 'completed').length 
        : 0;
      setCompletedWorksCount(completedContracts);
      
      setNavBadges({
        services: pendingQuotes + activeContracts,
        events: 0
      });
    } catch (error) {
      console.error('Error fetching nav badges:', error);
    }
  };

  const fetchPressPolicies = async () => {
    try {
      // Fetch policies for press users (including communication policies)
      const response = await api.get('/policies/public');
      // Filter policies that apply to press users
      const relevantPolicies = (response.data || []).filter(p => 
        p.policy_type === 'press' || 
        p.policy_type === 'communication' || 
        p.policy_type === 'Prensa' ||
        (p.title && p.title.toLowerCase().includes('prensa')) ||
        (p.title && p.title.toLowerCase().includes('strike'))
      );
      setPressPolicies(relevantPolicies);
    } catch (error) {
      console.error('Error fetching policies:', error);
    }
  };

  const fetchPressData = async () => {
    try {
      const response = await api.get('/press/me');
      setPress(response.data);
    } catch (error) {
      console.error('Error fetching press data:', error);
      toast.error('Error al cargar datos');
      localStorage.removeItem('press_token');
      navigate('/prensa/login');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('press_token');
    localStorage.removeItem('press_data');
    toast.success('Sesión cerrada');
    navigate('/prensa/login');
  };

  const handleBackToAccount = () => {
    navigate('/mi-cuenta');
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, badge: 0 },
    { id: 'packages', label: 'Mis Servicios', icon: Package, badge: navBadges.services },
    { id: 'portfolio', label: 'Portfolio', icon: Image, badge: 0 },
    { id: 'events', label: 'Eventos', icon: Calendar, badge: navBadges.events },
    { id: 'settlements', label: 'Liquidaciones', icon: DollarSign, badge: 0 },
    { id: 'djs', label: 'DJs', icon: Disc3, badge: 0 },
    { id: 'jobs', label: 'Portal Empleo', icon: Briefcase, badge: 0 },
    { id: 'preview', label: 'Vista Previa', icon: Eye, badge: 0 },
    { id: 'settings', label: 'Configuración', icon: Settings, badge: 0 },
  ];

  const stats = [
    { 
      label: 'Vistas del Perfil', 
      value: formatNumber(press?.profile_views || 0), 
      icon: Eye,
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
      editable: false
    },
    { 
      label: 'Alcance Mensual', 
      value: formatNumber(press?.statistics?.monthly_reach || 0), 
      icon: TrendingUp,
      color: 'text-green-400',
      bgColor: 'bg-green-500/10',
      editable: true,
      field: 'monthly_reach'
    },
    { 
      label: 'Vistas Stories', 
      value: formatNumber(press?.statistics?.average_story_views || 0), 
      icon: Play,
      color: 'text-pink-400',
      bgColor: 'bg-pink-500/10',
      editable: true,
      field: 'average_story_views'
    },
    { 
      label: 'Trabajos Realizados', 
      value: completedWorksCount, 
      icon: CheckCircle,
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/10',
      editable: false
    },
  ];

  const totalFollowers = press?.social_networks?.reduce((sum, sn) => sum + (sn.followers || 0), 0) || 0;

  return (
    <div
      className="min-h-screen bg-[#0A0A0A] press-portal-root"
      data-testid="press-dashboard-root"
    >
      {/* Apply 80% zoom on desktop (>=lg). Mobile keeps 100% for readability.
          `zoom` is the simplest cross-browser way (Chrome/Edge/Safari/Firefox)
          and preserves layout flow unlike `transform: scale`. */}
      <style>{`
        @media (min-width: 1024px) {
          .press-portal-root { zoom: 0.8; }
        }
      `}</style>
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-zinc-900/95 backdrop-blur-sm border-b border-zinc-800">
        <div className="flex items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Bravo" className="h-8" />
          </Link>
          <div className="flex items-center gap-2">
            <NotificationBell 
              userType="press" 
              apiEndpoint="/press/notifications" 
              title="Notificaciones" 
            />
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 text-zinc-400 hover:text-white transition-colors"
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-zinc-900/95 backdrop-blur-sm border-r border-zinc-800 z-40
        transform transition-transform duration-300 lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-zinc-800">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Bravo" className="h-10" />
              <div>
                <span className="text-white font-bold block">Portal</span>
                <span className="text-blue-400 text-sm">Servicios</span>
              </div>
            </Link>
          </div>

          {/* Press Info */}
          <div className="p-4 border-b border-zinc-800">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center overflow-hidden">
                {press?.logo_url ? (
                  <img src={press.logo_url} alt={press.media_name} className="w-full h-full object-cover" />
                ) : (
                  <Newspaper className="text-blue-400" size={24} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold truncate">{press?.media_name}</p>
                <p className="text-zinc-500 text-sm">{formatNumber(totalFollowers)} seguidores</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setActiveTab(item.id);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  activeTab === item.id
                    ? 'bg-blue-500/20 text-blue-400'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <div className="relative">
                  <item.icon size={20} />
                  {item.badge > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                      {item.badge > 9 ? '9+' : item.badge}
                    </span>
                  )}
                </div>
                <span className="font-medium flex-1">{item.label}</span>
                {item.badge > 0 && (
                  <span className="bg-red-500/20 text-red-400 text-xs font-bold px-2 py-0.5 rounded">
                    {item.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>

          {/* Back to Account */}
          <div className="p-4 border-t border-zinc-800">
            <button
              onClick={handleBackToAccount}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-zinc-400 hover:text-blue-400 hover:bg-blue-500/10 transition-all"
            >
              <ArrowLeft size={20} />
              <span className="font-medium">Mi Cuenta</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 pb-20 lg:pb-0 lg:pt-0">
        <div className="p-4 sm:p-6 lg:p-8">
          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-8"
            >
              {/* Welcome */}
              <div>
                <h1 className="text-2xl lg:text-3xl font-black text-white mb-2">
                  ¡Hola, {press?.media_name}!
                </h1>
                <p className="text-zinc-400">Bienvenido a tu panel de servicios</p>
              </div>

              {/* Press Policies / Strikes System Banner */}
              {pressPolicies.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`bg-gradient-to-r ${
                    policiesAccepted 
                      ? 'from-green-500/10 to-emerald-500/10 border-green-500/30' 
                      : 'from-red-500/10 to-orange-500/10 border-red-500/30'
                  } border rounded-2xl p-5`}
                  data-testid="policies-banner"
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 ${policiesAccepted ? 'bg-green-500/20' : 'bg-red-500/20'} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      {policiesAccepted ? (
                        <CheckCircle className="text-green-400" size={24} />
                      ) : (
                        <AlertCircle className="text-red-400" size={24} />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-white font-bold text-lg">
                          Sistema de Strikes y Normas de Comunicación
                        </h3>
                        {policiesAccepted && (
                          <span className="bg-green-500/20 text-green-400 text-xs px-2 py-0.5 rounded-full font-medium">
                            ✓ Aceptado
                          </span>
                        )}
                      </div>
                      <p className="text-zinc-400 text-sm mb-3">
                        {policiesAccepted 
                          ? 'Has aceptado las políticas vigentes de la plataforma.'
                          : 'Para garantizar la mejor experiencia en la plataforma, es importante que conozcas y aceptes las políticas vigentes.'
                        }
                      </p>
                      <div className="flex flex-wrap gap-2 items-center">
                        {pressPolicies.map((policy) => (
                          <Button
                            key={policy.id}
                            variant="outline"
                            size="sm"
                            className={policiesAccepted 
                              ? 'text-green-400 border-green-500/30 hover:bg-green-500/10'
                              : 'text-red-400 border-red-500/30 hover:bg-red-500/10'
                            }
                            onClick={() => {
                              setSelectedPolicy(policy);
                              setShowPolicyModal(true);
                            }}
                          >
                            <FileText size={14} className="mr-1" />
                            {policy.title}
                          </Button>
                        ))}
                        
                        {/* Accept Button - Only show if not accepted */}
                        {!policiesAccepted && (
                          <Button
                            size="sm"
                            className="bg-green-500 hover:bg-green-600 text-white ml-auto"
                            onClick={handleAcceptPolicies}
                            disabled={acceptingPolicies}
                            data-testid="accept-policies-btn"
                          >
                            {acceptingPolicies ? (
                              <>
                                <Loader2 className="animate-spin mr-1" size={14} />
                                Aceptando...
                              </>
                            ) : (
                              <>
                                <Check size={14} className="mr-1" />
                                Aceptar Políticas
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Stats Grid */}
              <div className="mb-2 flex items-center justify-between">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <BarChart3 className="text-blue-400" size={20} />
                  Estadísticas
                </h2>
                {!editingStats ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-zinc-400 border-zinc-700 hover:text-white hover:border-zinc-600"
                    onClick={() => setEditingStats(true)}
                    data-testid="edit-stats-btn"
                  >
                    <Edit2 size={14} className="mr-1" />
                    Editar
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-zinc-400 border-zinc-700"
                      onClick={() => {
                        setEditingStats(false);
                        // Reset form to current values
                        setStatsForm({
                          monthly_reach: press?.statistics?.monthly_reach || 0,
                          average_story_views: press?.statistics?.average_story_views || 0
                        });
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      size="sm"
                      className="bg-green-500 hover:bg-green-600 text-white"
                      onClick={handleSaveStats}
                      disabled={savingStats}
                      data-testid="save-stats-btn"
                    >
                      {savingStats ? (
                        <Loader2 size={14} className="animate-spin mr-1" />
                      ) : (
                        <Save size={14} className="mr-1" />
                      )}
                      Guardar
                    </Button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {stats.map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`bg-zinc-900/50 border ${stat.editable && editingStats ? 'border-blue-500/50' : 'border-zinc-800'} rounded-2xl p-5`}
                  >
                    <div className={`w-10 h-10 ${stat.bgColor} rounded-xl flex items-center justify-center mb-3`}>
                      <stat.icon className={stat.color} size={20} />
                    </div>
                    {stat.editable && editingStats ? (
                      <Input
                        type="number"
                        min="0"
                        value={statsForm[stat.field]}
                        onChange={(e) => setStatsForm({
                          ...statsForm,
                          [stat.field]: parseInt(e.target.value) || 0
                        })}
                        className="bg-zinc-800 border-zinc-700 text-white text-xl font-black mb-1 h-10"
                        data-testid={`stat-input-${stat.field}`}
                      />
                    ) : (
                      <p className="text-2xl font-black text-white">{stat.value}</p>
                    )}
                    <p className="text-zinc-500 text-sm flex items-center gap-1">
                      {stat.label}
                      {stat.editable && !editingStats && (
                        <Edit2 size={10} className="text-zinc-600" />
                      )}
                    </p>
                  </motion.div>
                ))}
              </div>

              {/* Quick Actions */}
              <div className="space-y-4">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <Sparkles className="text-violet-400" size={20} />
                  Accesos Rápidos
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    onClick={() => setActiveTab('djs')}
                    className="bg-gradient-to-br from-violet-500/20 to-purple-600/20 border border-violet-500/30 rounded-2xl p-5 text-left hover:border-violet-500/50 transition-all"
                  >
                    <div className="w-10 h-10 bg-violet-500/20 rounded-xl flex items-center justify-center mb-3">
                      <Disc3 className="text-violet-400" size={20} />
                    </div>
                    <h3 className="text-white font-bold mb-1">DJs</h3>
                    <p className="text-zinc-400 text-sm">Encontrar talentos</p>
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    onClick={() => setActiveTab('jobs')}
                    className="bg-gradient-to-br from-pink-500/20 to-rose-600/20 border border-pink-500/30 rounded-2xl p-5 text-left hover:border-pink-500/50 transition-all"
                  >
                    <div className="w-10 h-10 bg-pink-500/20 rounded-xl flex items-center justify-center mb-3">
                      <Briefcase className="text-pink-400" size={20} />
                    </div>
                    <h3 className="text-white font-bold mb-1">Portal Empleo</h3>
                    <p className="text-zinc-400 text-sm">Gestionar vacantes</p>
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    onClick={() => setActiveTab('packages')}
                    className="bg-gradient-to-br from-cyan-500/20 to-blue-600/20 border border-cyan-500/30 rounded-2xl p-5 text-left hover:border-cyan-500/50 transition-all"
                  >
                    <div className="w-10 h-10 bg-cyan-500/20 rounded-xl flex items-center justify-center mb-3">
                      <Package className="text-cyan-400" size={20} />
                    </div>
                    <h3 className="text-white font-bold mb-1">Mis Servicios</h3>
                    <p className="text-zinc-400 text-sm">Gestionar paquetes</p>
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    onClick={() => setActiveTab('preview')}
                    className="bg-gradient-to-br from-emerald-500/20 to-green-600/20 border border-emerald-500/30 rounded-2xl p-5 text-left hover:border-emerald-500/50 transition-all"
                  >
                    <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-3">
                      <Eye className="text-emerald-400" size={20} />
                    </div>
                    <h3 className="text-white font-bold mb-1">Vista Previa</h3>
                    <p className="text-zinc-400 text-sm">Ver tu perfil</p>
                  </motion.button>
                </div>
              </div>

              {/* Social Networks */}
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Instagram className="text-blue-400" size={20} />
                  Redes Sociales
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {press?.social_networks?.map((network, index) => (
                    <div key={index} className="bg-zinc-800/50 rounded-xl p-4 flex items-center gap-3">
                      <div className="w-10 h-10 bg-zinc-700 rounded-full flex items-center justify-center">
                        <Globe className="text-zinc-400" size={18} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium capitalize">{network.platform}</p>
                        <p className="text-zinc-500 text-sm truncate">
                          {network.username || network.url}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-bold">{formatNumber(network.followers)}</p>
                        <p className="text-zinc-500 text-xs">seguidores</p>
                      </div>
                    </div>
                  ))}
                </div>
                {(!press?.social_networks || press.social_networks.length === 0) && (
                  <p className="text-zinc-500 text-center py-8">
                    No tenés redes sociales configuradas. 
                    <button 
                      onClick={() => setActiveTab('settings')}
                      className="text-blue-400 hover:text-blue-300 ml-1"
                    >
                      Agregar ahora
                    </button>
                  </p>
                )}
              </div>

              {/* Quick Actions */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <button 
                  onClick={() => setActiveTab('packages')}
                  className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 text-left hover:border-blue-500/50 transition-all group"
                >
                  <Package className="text-blue-400 mb-3" size={24} />
                  <h3 className="text-white font-bold mb-1">Gestionar Servicios</h3>
                  <p className="text-zinc-500 text-sm">Agregá o editá tus paquetes de publicidad</p>
                  <ChevronRight className="text-zinc-600 group-hover:text-blue-400 mt-3 transition-colors" size={20} />
                </button>

                <button 
                  onClick={() => setActiveTab('portfolio')}
                  className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 text-left hover:border-purple-500/50 transition-all group"
                >
                  <Image className="text-purple-400 mb-3" size={24} />
                  <h3 className="text-white font-bold mb-1">Tu Portfolio</h3>
                  <p className="text-zinc-500 text-sm">Mostrá tus mejores trabajos anteriores</p>
                  <ChevronRight className="text-zinc-600 group-hover:text-purple-400 mt-3 transition-colors" size={20} />
                </button>

                <button 
                  onClick={() => setActiveTab('events')}
                  className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 text-left hover:border-green-500/50 transition-all group"
                >
                  <Calendar className="text-green-400 mb-3" size={24} />
                  <h3 className="text-white font-bold mb-1">Ver Eventos</h3>
                  <p className="text-zinc-500 text-sm">Explorá eventos para ofrecer tus servicios</p>
                  <ChevronRight className="text-zinc-600 group-hover:text-green-400 mt-3 transition-colors" size={20} />
                </button>

                <button 
                  onClick={() => {
                    // Navigate to packages tab with contracts subtab
                    setActiveTab('packages');
                    // Small delay to ensure tab switch happens first, then switch to contracts subtab
                    setTimeout(() => {
                      window.dispatchEvent(new CustomEvent('switchToContractsSubTab'));
                    }, 200);
                  }}
                  className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 text-left hover:border-yellow-500/50 transition-all group"
                >
                  <FileText className="text-yellow-400 mb-3" size={24} />
                  <h3 className="text-white font-bold mb-1">Contratos Activos</h3>
                  <p className="text-zinc-500 text-sm">Gestioná tus contratos con productoras</p>
                  <ChevronRight className="text-zinc-600 group-hover:text-yellow-400 mt-3 transition-colors" size={20} />
                </button>
              </div>
            </motion.div>
          )}

          {/* Packages Tab */}
          {activeTab === 'packages' && (
            <PackagesTab press={press} onRefresh={fetchPressData} />
          )}

          {/* Portfolio Tab */}
          {activeTab === 'portfolio' && (
            <PortfolioTab press={press} onRefresh={fetchPressData} />
          )}

          {/* Events Tab */}
          {activeTab === 'events' && (
            <PressEventsTab press={press} />
          )}

          {/* DJs Tab */}
          {activeTab === 'djs' && (
            <DJsTab />
          )}

          {/* Jobs Tab */}
          {activeTab === 'jobs' && (
            <JobsTab press={press} />
          )}

          {/* Profile Preview Tab */}
          {activeTab === 'preview' && (
            <ProfilePreviewTab press={press} />
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <SettingsTab press={press} onRefresh={fetchPressData} />
          )}

          {/* Settlements Tab */}
          {activeTab === 'settlements' && (
            <SettlementsTab press={press} />
          )}
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 lg:hidden z-50" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        {/* Solid background */}
        <div className="absolute inset-0 bg-[#0A0A0A]" />
        {/* Glassmorphism effect */}
        <div className="absolute inset-0 backdrop-blur-xl bg-zinc-900/90 border-t border-zinc-700" />
        
        <div className="relative flex items-center justify-around py-2 px-2">
          {[
            { id: 'dashboard', icon: LayoutDashboard, label: 'Inicio', color: 'blue', badge: 0 },
            { id: 'packages', icon: Package, label: 'Servicios', color: 'purple', badge: navBadges.services },
            { id: 'events', icon: Calendar, label: 'Eventos', color: 'green', badge: navBadges.events },
            { id: 'settings', icon: Settings, label: 'Config', color: 'zinc', badge: 0 },
          ].map((item) => {
            const isActive = activeTab === item.id;
            const colors = {
              blue: { active: 'text-blue-400', bg: 'bg-blue-500/20', indicator: 'bg-blue-400' },
              green: { active: 'text-green-400', bg: 'bg-green-500/20', indicator: 'bg-green-400' },
              purple: { active: 'text-purple-400', bg: 'bg-purple-500/20', indicator: 'bg-purple-400' },
              zinc: { active: 'text-zinc-300', bg: 'bg-zinc-700/50', indicator: 'bg-zinc-400' },
            };
            const colorClass = colors[item.color];
            
            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`relative flex flex-col items-center gap-0.5 px-4 py-1.5 rounded-xl transition-all duration-200 flex-1 ${
                  isActive
                    ? `${colorClass.active}`
                    : 'text-zinc-500'
                }`}
                data-testid={`mobile-nav-${item.id}`}
              >
                {/* Active indicator */}
                {isActive && (
                  <span className={`absolute -top-0.5 w-5 h-0.5 ${colorClass.indicator} rounded-full`} />
                )}
                
                <div className="relative">
                  <item.icon size={22} strokeWidth={isActive ? 2.5 : 1.5} />
                  {item.badge > 0 && (
                    <span className="absolute -top-1 -right-1.5 min-w-[14px] h-[14px] bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-0.5">
                      {item.badge > 9 ? '9+' : item.badge}
                    </span>
                  )}
                </div>
                <span className={`text-[9px] font-semibold uppercase tracking-wider ${isActive ? colorClass.active : 'text-zinc-500'}`}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Policy Modal */}
      <Dialog open={showPolicyModal} onOpenChange={setShowPolicyModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[80vh] overflow-y-auto" aria-describedby="policy-description">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertCircle className="text-red-400" size={20} />
              {selectedPolicy?.title}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4" id="policy-description">
            <div className="prose prose-invert prose-sm max-w-none">
              <div 
                className="text-zinc-300 whitespace-pre-wrap"
                dangerouslySetInnerHTML={{ __html: selectedPolicy?.content?.replace(/\n/g, '<br />') }}
              />
            </div>
            
            {selectedPolicy?.created_at && (
              <p className="text-zinc-500 text-xs mt-6 pt-4 border-t border-zinc-800">
                Última actualización: {new Date(selectedPolicy.created_at).toLocaleDateString('es-AR')}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button onClick={() => setShowPolicyModal(false)}>
              Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PressDashboardPage;
